import { CONTENT_ASSETS, CONTENT_TEMPLATE_PRESETS, ContentDocumentError, WORKSPACE_DOCUMENT_LIMITS, isStoredAsset, parseContentDocument, searchContentAssets, serializeContentDocument, validateContentDocument, validateWorkspaceAsset } from './document.mjs';
import { embedStoredAssets } from './stored-assets.mjs';
import { blockedBookMessage, blockedBookPages, bookPageNote, bookPageStates } from './book-text.mjs';
import { loadPrintMetrics, printMetrics } from './print-wrap.mjs';
import { createContentTemplateLibrary, findTemplateSession, listLocalAssets, listLocalDrafts, readLocalDraft, saveLocalAsset, saveLocalDraft, readWorkspaceSession, saveWorkspaceSession } from './persistence.mjs';
import { readStudioAddress, studioAddress } from './studio-address.mjs';
import { SAJU_BOOK_PICTURE_SOURCE, importSajuCanvas } from './saju-canvas-import.mjs';
import { createCreatorClient, updateCreatorLecture } from './client.mjs';
import { mountTemplatePicker } from './template-picker.mjs';
import { mountOutputPanel, pageRange } from './output-panel.mjs';
import { lockedNote, lockedRole, outputsFor, pagePerSection, readOnlyReason, templatePageCounts } from './studio-families.mjs';
import { arrangeSourceText } from './material.mjs';
import { hasCanvasContent, mountContentReader } from './reader.mjs';
import { mountPenpotTypography } from './penpot-typography.mjs';
import { mountPenpotLibrary } from './penpot-library.mjs';
import { getEditorSample } from './editor-samples.mjs';
import { assertPenpotTemplateReady, materializePenpotTemplateSeed } from './penpot-template-seed.mjs';
import { mountNativeContentPanel } from './native-content-panel.mjs';
import { createBlockSuiteContentSession } from './blocksuite-content-session.mjs';
import { mountCanvasProperties } from './canvas-properties.mjs';
import { mountLearningAuthor } from './learning-author.mjs';
import { getContentComponent, validateSectionComponents } from './components.mjs';
import { SUBJECT_KEYS, sameSubject, validateSubject } from './component-saju.mjs';
import { mountComponentPanel } from './component-panel.mjs';
import { createNarrativeCommands } from './narrative-commands.mjs';
import { upsertPenpotDataComponent, removePenpotDataComponent, detachPenpotDataComponent, readPenpotDataComponents } from './component-penpot.mjs';
import { agentError, agentObject, agentString, callPenpotMcp, createAgentCommandQueue, mountAgentConnection } from './agent-bridge.mjs';

const el = (tag, attrs = {}, children = []) => {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (key === 'text') node.textContent = value;
    else if (key.startsWith('on')) node.addEventListener(key.slice(2), value);
    else if (value !== undefined) node.setAttribute(key, String(value));
  }
  node.append(...children);
  return node;
};
const option = (value, text) => el('option', { value, text });
const uuid = () => crypto.randomUUID();
const modeOf = section => section.kind === 'canvas' ? 'edgeless' : 'page';
const kindLabel = kind => ({ document: '글 문서', canvas: '캔버스 편집', penpot: '디자인 페이지' })[kind];
const sourceImageBytes = section => section.kind === 'penpot'
  ? section.preview ? atob(section.preview.data).length : 0
  : section.assets.reduce((sum, asset) => sum + (isStoredAsset(asset) ? asset.size : atob(asset.data).length), 0);

/** A composition owns ordered documents and canvases; each section keeps its native source. */
/**
 * `routing`: the Studio owns the page's address (the standalone /learn/studio page): it names
 * the template being edited, and opening that address opens it again (studio-address.mjs).
 */
export function mountWorkspace(root, { initialMarkdown = '', onChange, getAccessToken, routing = false } = {}) {
  let content = { schemaVersion: 2, format: 'blocksuite', title: '', sections: [] };
  let engine, editor, reader, nativeEditor, nativeSectionId, contentSession, activeId, id = uuid(), revision = 0;
  let dirty = false, disposed = false, busy = false, transferring = false, timer, generation = 0, records = [], templateRecords = [], registeredAssets = [];
  let sharedAssetError = null, sharedAssetAvailable = false;
  const registeredAssetUrls = new Map();
  let activeRun = null, emission = Promise.resolve(), displayedError = null;
  let documentBatch = 0, emitPending = false;
  let componentRead = Promise.resolve();
  let previewing = false, courseDraft = null, courseId = '', publishedDraftIds = new Set();
  let editingView = 'content';
  let outputChanged = () => {};
  let viewingBook = false;
  // The template the open content was made from (its address), or null; while its output is
  // shown in the editor, which output and page (the rest of the address).
  let templateOrigin = null, outputView = null, enteringOutput = false;
  // Reading the book in the editor: the Studio page the book page being read comes from, and the
  // number of that book page.
  let readingSectionId = null, readingPage = null;
  // The page templates of the output shown in the editor, when it lays out its own pages (a Saju
  // book): the Studio's page list is then these, each edited on its book pages.
  let shownTemplates = [];
  const address = routing ? readStudioAddress(window.location.pathname) : null;
  const compactViewport = window.matchMedia('(max-width: 850px)');
  let panelOpener = null, pendingFocus = null;
  let initialized = false, recoveredGeneration = -1, recoveryWrite = Promise.resolve();
  const sessionKey = 'edusense-content-studio-session';
  const sessionId = uuid();
  let previousSessionId = null;
  try { previousSessionId = window.sessionStorage.getItem(sessionKey); } catch { /* IndexedDB can still restore the latest working copy. */ }
  const verifiedDesigns = new WeakMap();
  const client = createCreatorClient({ getAccessToken });
  const templates = createContentTemplateLibrary({ remote: client.templates });
  const active = () => content.sections.find(section => section.id === activeId);
  const status = el('span', { role: 'status', 'aria-live': 'polite', 'data-testid': 'studio-status', text: '편집기를 준비하고 있습니다…' });
  const error = el('p', { role: 'alert', class: 'es-work-error', hidden: '', 'data-testid': 'studio-error' });
  const outline = el('div', { class: 'es-work-outline', 'aria-label': '콘텐츠 순서' });
  const host = el('div', { class: 'es-work-editor', 'data-testid': 'studio-editor' });
  const preview = el('div', { class: 'es-work-preview', hidden: '', 'data-testid': 'studio-preview' });
  const title = el('input', { 'aria-label': '콘텐츠 제목', placeholder: '제목 없는 콘텐츠', maxlength: 200, 'data-testid': 'studio-title', oninput: () => { content.title = title.value; changed(); } });
  const sectionTitle = el('input', { 'aria-label': '선택한 페이지 제목', maxlength: 200, placeholder: '페이지 이름', oninput: () => { if (active()) { active().title = sectionTitle.value; renderOutline(); changed(); } } });
  const draftSelect = el('select', { 'aria-label': '저장한 콘텐츠', 'data-testid': 'studio-drafts' });
  const pageKind = el('span', { class: 'es-work-page-kind', 'data-testid': 'studio-page-kind' });
  const pageSelect = el('select', { 'aria-label': '현재 페이지', 'data-testid': 'studio-page-select', onchange: () => {
    const entryId = pageSelect.value;
    void run(() => goToEntry(entryId)).finally(renderOutline);
  } });
  const pagesHeading = el('h2', { text: '페이지', 'data-testid': 'studio-page-count' });
  // What the page menu shows while no page template is in view (the book's page list).
  const noEntry = el('option', { value: '', hidden: '', disabled: '', text: '쪽 틀을 고르세요' });
  // A content whose output lays out its own pages (a Saju book) is edited on those pages.
  const pagesEdited = () => Boolean(outputsFor(content)[0]?.output.templatesOf);
  const CANVAS = 'canvas';
  /**
   * What the page list, the page menu and the pager step through: the Studio pages, or, while an
   * output that lays out its own pages is shown, its page templates. `at` is the one in view (-1: none).
   */
  function listing() {
    if (viewingBook && shownTemplates.length) {
      return { templates: true, entries: shownTemplates.map(template => ({ id: `template:${template.id}`, template })),
        at: shownTemplates.findIndex(template => template.pages.includes(readingPage)) };
    }
    const shown = viewingBook && readingSectionId ? readingSectionId : activeId;
    return { templates: false, entries: content.sections.map(section => ({ id: section.id, section })), at: content.sections.findIndex(section => section.id === shown) };
  }
  // Reading the book, a Studio page goes to the book pages it prints (or, if it prints none,
  // opens it for editing); editing, it opens. A content edited on its output's pages opens none of
  // its Studio pages: from its canvas (where an agent's select_section leaves it) a page goes back
  // to the book, to the page it prints. A page template opens its first page (or stays where it is,
  // when the page in view is one of its pages).
  async function goToPage(sectionId) {
    if (viewingBook && outputPanel.reveal(sectionId)) return;
    if (!pagesEdited()) { await select(sectionId); return; }
    if (!viewingBook) { await showBook({ read: true }); outputPanel.reveal(sectionId); }
  }
  function goToEntry(entryId) {
    const template = viewingBook ? shownTemplates.find(item => `template:${item.id}` === entryId) : null;
    if (!template) return goToPage(entryId);
    if (!template.pages.includes(readingPage) || !outputPanel.revealPage(readingPage)) outputPanel.revealPage(template.pages[0]);
    return undefined;
  }
  const stepPage = offset => {
    const { entries, at } = listing();
    const target = entries[at + offset];
    return target ? goToEntry(target.id) : undefined;
  };
  const pagePrevious = el('button', { type: 'button', class: 'es-work-pager-step', 'aria-label': '이전 페이지', 'data-testid': 'studio-page-prev', text: '‹',
    onclick: () => { void run(() => stepPage(-1)); } });
  const pagePosition = el('span', { class: 'es-work-pager-position', 'aria-live': 'polite', 'data-testid': 'studio-page-position' });
  const pageNext = el('button', { type: 'button', class: 'es-work-pager-step', 'aria-label': '다음 페이지', 'data-testid': 'studio-page-next', text: '›',
    onclick: () => { void run(() => stepPage(1)); } });
  const pager = el('div', { class: 'es-work-pager', role: 'group', 'aria-label': '페이지 이동' }, [pagePrevious, pagePosition, pageNext]);
  const fail = reason => { displayedError = reason; error.textContent = reason instanceof Error ? reason.message : String(reason); error.hidden = false; };
  const announce = text => { status.textContent = text; };
  const button = (text, action, attrs = {}) => el('button', { type: 'button', text, onclick: () => { void run(action); }, ...attrs });
  const field = (label, input) => el('label', { class: 'es-work-field' }, [el('span', { text: label }), input]);
  const addPages = el('div', { class: 'es-work-add-pages' }, [button('+ 캔버스', () => addSection('canvas')),
    button('+ 글 문서', () => addSection('document'), { 'data-testid': 'studio-add-document' }),
    button('+ 디자인', async () => { await addSection('penpot'); await setEditingView('design'); }, { 'data-testid': 'studio-add-design' })]);
  const mayReplace = () => !dirty || window.confirm('저장하지 않은 내용이 있습니다. 다른 콘텐츠를 열까요?');
  function refreshContentPanel() {
    if (disposed) return;
    canvasProperties.refresh();
    void nativeContent.refresh().catch(fail);
  }
  function renderEditingView() {
    const native = active()?.kind === 'penpot';
    root.dataset.editingView = editingView;
    root.classList.toggle('es-workspace--content', native && editingView === 'content');
    contentViewButton.setAttribute('aria-pressed', String(editingView === 'content'));
    designViewButton.setAttribute('aria-pressed', String(editingView === 'design'));
    taskViews.hidden = !native;
    const canvas = active()?.kind === 'canvas';
    nativeContent.element.hidden = !native && !canvas;
    nativeContent.listElement.hidden = (!native && !canvas) || editingView !== 'content';
    legacyNotice.hidden = native || canvas;
    canvasProperties.refresh();
    componentAuthor.refresh();
    designTab.textContent = '글 · 디자인';
    pageKind.textContent = kindLabel(active()?.kind) ?? '';
    renderAssetGallery();
    nativeLayersButton.hidden = !native || editingView !== 'design';
    nativePropertiesButton.hidden = !native || editingView !== 'design';
    renderPanels();
  }
  async function applyEditingView() {
    renderEditingView();
    if (nativeEditor?.getStatus().connected) {
      await nativeEditor.execute('set_editing_view', { view: editingView });
    }
    await nativeContent.refresh();
  }
  async function setEditingView(view) {
    if (!['content', 'design'].includes(view)) throw agentError('INVALID_INPUT', 'content 또는 design 작업 화면을 선택해 주세요.');
    if (previewing) await showActive();
    // Task views never change a page's source engine. Migration is an explicit
    // operation in page settings, including for existing block documents.
    if (view === 'design' && active()?.kind !== 'penpot') throw agentError('UNSUPPORTED_SECTION', '이 페이지는 BlockSuite 문서입니다. 디자인 페이지를 추가하거나 페이지 설정에서 원본을 옮겨 주세요.');
    editingView = view;
    root.dataset.panels = 'auto'; root.dataset.nativePanel = 'none'; showInspector('design');
    await applyEditingView();
    announce(view === 'design' ? '디자인 편집 · 원본 레이어와 레이아웃' : '콘텐츠 편집 · 글과 그림을 선택해 수정하세요');
  }
  async function flushNative() {
    await outputPanel.flush();
    if (!nativeEditor) return;
    await nativeEditor.ready;
    const state = nativeEditor.getStatus();
    if (state.connected || state.dirty) await nativeEditor.flush();
  }
  function disposeNative() { nativeEditor?.dispose(); nativeEditor = null; nativeSectionId = null; }
  function receiveComponents(section, components) {
    validateSectionComponents(components, 'components', (path, message) => { throw agentError('INVALID_COMPONENT', `${path}: ${message}`); });
    if (JSON.stringify(section.components ?? []) === JSON.stringify(components)) return;
    section.components = structuredClone(components);
    if (active() === section) componentAuthor.refresh();
  }
  async function syncComponents(section = active(), current = editor, native = nativeEditor) {
    if (!section || disposed || active() !== section) return Promise.resolve();
    if (section.kind !== 'penpot') {
      if (current && current === editor && current.getComponents) receiveComponents(section, current.getComponents());
      return Promise.resolve();
    }
    // Native keyboard undo changes the editor source directly. Read its record in
    // order, just as capture reads its drawing, instead of maintaining a second history.
    const read = componentRead.catch(() => {}).then(async () => {
      if (disposed || active() !== section || nativeEditor !== native || !native?.getStatus().connected) return;
      const components = await readPenpotDataComponents({ editor: native, assertCurrent: () => {
        if (disposed || active() !== section || nativeEditor !== native) throw agentError('STALE_GENERATION', '컴포넌트 정보를 읽는 동안 페이지가 바뀌었습니다.');
      } });
      if (!disposed && active() === section && nativeEditor === native) receiveComponents(section, components);
    });
    componentRead = read;
    return read;
  }
  function componentChanged(section, current, native) {
    if (disposed || active() !== section) return;
    changed();
    void syncComponents(section, current, native).catch(reason => { if (!disposed && active() === section) fail(reason); });
  }
  async function capture() {
    await outputPanel.flush();
    const current = editor, section = active();
    if (current && section) {
      const source = await current.getSnapshot();
      if (current === editor && section === active()) Object.assign(section, source);
    }
    await syncComponents(section, current, nativeEditor);
    if (!documentBatch) serializeContentDocument(content);
    else {
      const checked = validateContentDocument(content, { documentRules: false });
      if (!checked.valid) throw new ContentDocumentError(checked.issues);
    }
    return structuredClone(content);
  }
  // A change made page by page that passes through a state the rules across pages refuse — one scope's manuscript
  // written on some of its pages (narrative-commands.mjs) — is one change: those rules are checked, and the document
  // saved and kept, when it is done.
  async function asOneChange(action) {
    documentBatch += 1;
    let result;
    try { result = await action(); } finally { documentBatch -= 1; }
    if (!documentBatch) {
      serializeContentDocument(content);
      if (emitPending) { emitPending = false; await emit(); }
    }
    return result;
  }
  function emit() {
    if (documentBatch) { emitPending = true; return Promise.resolve(structuredClone(content)); }
    // Serialize asynchronous asset reads so an older snapshot cannot overwrite a newer one.
    emission = emission.catch(() => {}).then(async () => {
      if (disposed) throw new Error('닫힌 편집기입니다.');
      const value = await capture();
      if (!disposed) await onChange?.(serializeContentDocument(value));
      return value;
    });
    return emission;
  }
  function preserveSession() {
    if (!initialized || disposed || transferring) return Promise.resolve();
    recoveryWrite = recoveryWrite.catch(() => {}).then(async () => {
      if (disposed || transferring) return;
      if (nativeEditor?.getStatus().dirty) await flushNative();
      const version = generation, draftId = id, templateId = templateOrigin;
      const document = await emit();
      if (disposed || transferring || generation !== version || id !== draftId) return;
      await saveWorkspaceSession({ sessionId, previousSessionId, draftId, revision, document,
        activeSectionId: activeId, editingView, dirty, templateId });
      previousSessionId = null;
      try { window.sessionStorage.setItem(sessionKey, sessionId); } catch { /* The working copy remains available in IndexedDB. */ }
      if (generation === version && id === draftId) recoveredGeneration = version;
    });
    return recoveryWrite;
  }
  function schedulePreservation() {
    clearTimeout(timer);
    if (transferring || disposed) return;
    timer = setTimeout(() => {
      if (busy || documentBatch) { schedulePreservation(); return; }
      void preserveSession().then(() => {
        if (!disposed && !busy && dirty && recoveredGeneration === generation) announce('작업 자동 보관됨');
      }).catch(fail);
    }, 500);
  }
  async function flush() {
    await ready;
    if (activeRun) await activeRun;
    if (disposed) throw new Error('닫힌 편집기입니다.');
    clearTimeout(timer);
    let value;
    await run(async () => {
      await captureReaderDocument();
      value = await emit();
    }, { propagate: true });
    return value;
  }
  function changed() {
    if (disposed) return;
    dirty = true; generation += 1;
    if (!transferring) announce('저장하지 않은 변경 사항');
    schedulePreservation();
  }
  function run(action, { propagate = false } = {}) {
    if (busy || disposed) return propagate ? Promise.reject(agentError('BUSY', '편집 작업이 진행 중입니다.')) : activeRun || Promise.resolve();
    busy = true; error.hidden = true; displayedError = null;
    const controls = [...root.querySelectorAll('.es-work-chrome button,.es-work-chrome input,.es-work-chrome select,.es-work-chrome textarea')].filter(control => !control.closest('.es-agent-connection'));
    const disabled = controls.map(control => control.disabled);
    controls.forEach(control => { control.disabled = true; });
    host.inert = true; root.setAttribute('aria-busy', 'true');
    activeRun = (async () => {
      try { await action(); }
      catch (reason) { fail(reason); announce('완료하지 못했습니다 · 현재 내용은 유지됩니다'); if (propagate) throw reason; }
      finally {
        try { await preserveSession(); } catch (reason) { fail(reason); }
        controls.forEach((control, index) => { control.disabled = disabled[index]; });
        host.inert = false; root.removeAttribute('aria-busy'); busy = false; activeRun = null;
        renderPager();
        componentAuthor.refresh();
        pendingFocus?.focus(); pendingFocus = null;
      }
    })();
    return activeRun;
  }
  // Where this page is in the content, everywhere pages are listed: 3 / 8. Run after a
  // busy run too, which puts back the buttons' earlier state.
  function renderPager() {
    const { templates, entries, at } = listing();
    const total = entries.length;
    pagesHeading.textContent = templates ? `페이지 · ${total}쪽 (책 ${shownTemplates.reduce((sum, template) => sum + template.pages.length, 0)}쪽)` : `페이지 · ${total}쪽`;
    pagePosition.textContent = `${at < 0 ? '–' : at + 1} / ${total}`;
    pagePrevious.disabled = busy || at <= 0; pageNext.disabled = busy || at >= total - 1;
    const value = entries[at]?.id ?? '';
    if (pageSelect.value !== value) pageSelect.value = value;
  }
  /** Marks the entry in view in the page list. */
  function markCurrent() {
    const { at } = listing();
    [...outline.children].forEach((node, index) => node.setAttribute('aria-current', index === at ? 'page' : 'false'));
  }
  // A Saju book's narrative components are judged with the print metrics (their text over its lines): read them once a
  // document holds one, and note the pages again when they are in.
  let printMetricsLoading = null;
  function awaitPrintMetrics() {
    if (printMetrics() || printMetricsLoading || !content.sections.some(section => section.components?.some(component => component.type === 'saju.narrative'))) return;
    printMetricsLoading = loadPrintMetrics().then(() => { if (!disposed) renderOutline(); }, () => { printMetricsLoading = null; });
  }
  function renderOutline() {
    awaitPrintMetrics();
    const { templates, entries, at } = listing();
    const total = entries.length;
    pageSelect.replaceChildren(...entries.map(({ id: entryId, section, template }, index) => option(entryId, template
      ? `${index + 1}/${total} · ${template.title} · ${pageRange(template.pages)}`
      : `${index + 1}/${total} · ${section.title || `페이지 ${index + 1}`} · ${kindLabel(section.kind)}`)), noEntry);
    pageSelect.value = entries[at]?.id ?? '';
    renderPager();
    pageKind.textContent = templates ? '책 쪽' : kindLabel(active()?.kind) ?? '';
    outputChanged();
    // A page template lists the book pages printed from it; a Studio page, what kind of page it is — and, on a Saju book,
    // what keeps it from the outputs or is not reviewed yet (book-text.mjs).
    const bookStates = new Map(bookPageStates(content).map(state => [content.sections[state.index], bookPageNote(state)]));
    outline.replaceChildren(...entries.map(({ id: entryId, section, template }, index) => {
      const row = button('', () => goToEntry(entryId), template
        ? { class: 'es-work-page es-work-page--template', 'data-template-id': template.id, 'data-testid': 'studio-page-template' }
        : { class: 'es-work-page', 'data-section-id': section.id });
      const about = template
        ? [pageRange(template.pages), template.pages.length > 1 ? `${template.pages.length}쪽` : '', template.chapter && !template.title.startsWith(template.chapter.name) ? template.chapter.name : ''].filter(Boolean).join(' · ')
        : readOnlyReason(section) ? `${kindLabel(section.kind)} · 읽기 전용` : kindLabel(section.kind);
      const book = template ? null : bookStates.get(section);
      if (book) row.dataset.bookText = book.kind;
      row.append(el('span', { class: 'es-work-page-number', 'aria-hidden': 'true', text: String(index + 1) }),
        el('span', {}, [el('strong', { text: template ? template.title : section.title || `페이지 ${index + 1}` }),
          el('small', { text: book ? `${about} · ${book.note}` : about })]));
      return row;
    }));
    markCurrent();
    // New pages are Studio pages: added from the canvas.
    addPages.hidden = templates;
  }
  async function showActive() {
    await outputPanel.flush();
    readOnlyNotice.hidden = true;
    if (viewingBook) {
      viewingBook = false; outputPanel.leave(); bookHost.hidden = true; outputView = null; readingSectionId = null; readingPage = null; shownTemplates = [];
      root.classList.remove('es-workspace--book');
      syncAddress(); renderOutline();
    }
    reader?.dispose(); reader = null;
    previewing = false; preview.hidden = true; host.hidden = false;
    root.classList.remove('es-workspace--reading');
    toolbar.hidden = false;
    previewButton.textContent = '미리보기';
    contentSession?.dispose(); contentSession = null; editor?.dispose(); editor = null;
    nativeEditor?.detach();
    host.replaceChildren();
    const section = active();
    if (!section || disposed) return;
    sectionTitle.value = section.title;
    const isPenpot = section.kind === 'penpot';
    root.classList.toggle('es-workspace--penpot', isPenpot);
    renderPanels();
    learningAuthor.refresh();
    reflectDesignButton.hidden = !isPenpot;
    toolbar.hidden = isPenpot;
    altField.hidden = !isPenpot; altText.value = isPenpot ? section.alt : '';
    host.dataset.mode = section.kind;
    mode.value = section.kind;
    toolbar.querySelectorAll('[data-canvas-only]').forEach(control => { control.hidden = section.kind !== 'canvas'; });
    announce('페이지를 여는 중…');
    if (isPenpot) {
      canvasNotice.hidden = true;
      if (nativeEditor && nativeSectionId === section.id) nativeEditor.attach(host);
      else {
        await flushNative(); disposeNative(); nativeSectionId = section.id;
        let nativeError = null;
        nativeEditor = mountPenpotLibrary(host, {
          source: section.source,
          name: `${content.title.trim() || '제목 없는 콘텐츠'} · ${section.title.trim() || '새 디자인'}`.slice(0, 250),
          onStatus: message => {
            if (active() !== section) return;
            announce(message);
            const state = nativeEditor?.getStatus();
            if (nativeError && state?.connected && state.error === null) {
              // Native recovery must not dismiss a newer error from the host.
              if (displayedError === nativeError) { error.hidden = true; error.textContent = ''; displayedError = null; }
              nativeError = null;
            }
          },
          onError: reason => { if (active() === section) { nativeError = reason; fail(reason); } },
          onChange: () => {
            if (!content.sections.includes(section)) return;
            section.preview = null; componentChanged(section, null, nativeEditor);
          },
          onStateChange: () => { if (active() === section) refreshContentPanel(); },
          onSourceChange: source => {
            if (!content.sections.includes(section)) return;
            const candidate = structuredClone(content);
            Object.assign(candidate.sections.find(item => item.id === section.id), { source, preview: null });
            serializeContentDocument(candidate);
            section.source = source; section.preview = null; changed();
          },
        });
      }
      await nativeEditor.ready;
      await syncComponents(section, null, nativeEditor);
      await applyEditingView();
      if (nativeEditor.getStatus().connected) announce(dirty ? '저장하지 않은 변경 사항' : '디자인 편집 준비 완료');
      renderOutline();
      return;
    }
    // A page whose text the output computes (the Saju book's 원문 근거) opens read only.
    const locked = readOnlyReason(section);
    const session = await engine.mount(host, { mode: modeOf(section), snapshot: section.snapshot, assets: section.assets, readonly: Boolean(locked), onChange: () => componentChanged(section, editor, null),
      onSelectionChange: () => { if (editor && active() === section) refreshContentPanel(); } });
    if (disposed) { session.dispose(); return; }
    editor = session;
    if (section.kind === 'canvas') contentSession = createBlockSuiteContentSession({ editor: session, fileId: id, pageId: section.id });
    Object.assign(section, await session.getSnapshot());
    canvasNotice.hidden = section.kind !== 'document' || !hasCanvasContent(section.snapshot);
    // A read-only page says why; a page with locked text says where that text is edited.
    const note = locked ?? lockedNote(section);
    readOnlyNotice.hidden = !note; readOnlyNotice.textContent = note ?? '';
    toolbar.hidden = Boolean(locked);
    renderOutline();
    editingView = 'content'; renderEditingView(); await nativeContent.refresh();
    announce(dirty ? '저장하지 않은 변경 사항' : '편집 준비 완료');
  }
  async function select(sectionId) {
    await flushNative(); await capture(); activeId = sectionId; generation += 1; await showActive();
    syncAddress();
  }
  async function addSection(kind) {
    if (content.sections.length >= WORKSPACE_DOCUMENT_LIMITS.sections) throw new Error(`한 콘텐츠에 최대 ${WORKSPACE_DOCUMENT_LIMITS.sections}페이지를 만들 수 있습니다.`);
    if (content.sections.length) { await flushNative(); await capture(); }
    if (kind === 'penpot') { content.schemaVersion = 3; content.format = 'mixed'; }
    const section = kind === 'penpot'
      ? { id: uuid(), kind, title: '새 디자인', source: null, preview: null, alt: '' }
      : { id: uuid(), kind, title: kind === 'canvas' ? '새 캔버스' : '새 문서', assets: [] };
    content.sections.push(section); activeId = section.id;
    await showActive(); changed();
  }
  async function convert(value, kind = 'canvas') {
    if ([2, 3].includes(value.schemaVersion)) return structuredClone(value);
    // A book-like template (a Saju product): one canvas page per section, so the page panel moves between them.
    if (kind === 'canvas' && pagePerSection(value.templateId) && value.sections.length > 1) {
      const pages = await engine.fromEditorialPages(value, { locked: role => lockedRole(value.templateId, role) });
      return { schemaVersion: 2, format: 'blocksuite', title: value.title,
        sections: pages.map(({ title, snapshot, assets, origin }) => ({ id: uuid(), kind, title, snapshot, assets, origin })) };
    }
    const source = await engine.fromEditorial(value, { mode: kind === 'canvas' ? 'edgeless' : 'page' });
    return { schemaVersion: 2, format: 'blocksuite', title: value.title, sections: [{ id: uuid(), kind, title: value.title || '가져온 자료', ...source }] };
  }
  /** Writes what is open into the address; `push` makes a history entry (entering the output view). */
  function syncAddress({ push = false } = {}) {
    if (!address) return;
    const editorAddress = { canvas: CANVAS, penpot: 'design', document: 'document' }[active()?.kind];
    const editing = editorAddress ? { output: editorAddress, page: content.sections.findIndex(section => section.id === activeId) + 1 } : {};
    const path = studioAddress(address.base, templateOrigin, viewingBook ? outputView ?? {} : editing);
    // The output view shows its own address, to copy and open again.
    bookAddress.hidden = !viewingBook || !templateOrigin;
    bookAddressPath.textContent = `${window.location.host}${path}`;
    if (window.location.pathname === path) return;
    const url = `${path}${window.location.search}${window.location.hash}`;
    if (push) window.history.pushState(window.history.state, '', url);
    else window.history.replaceState(window.history.state, '', url);
  }
  // Back and forward move between editing and the output view of the same template. A step
  // still running finishes first; then the address as it is by then is followed.
  function followAddress() {
    if (!address || !initialized) return;
    void (async () => {
      while (activeRun) await activeRun.catch(() => {});
      const next = readStudioAddress(window.location.pathname);
      if (!next || next.templateId !== templateOrigin) return;
      await run(() => openAddress(next));
    })();
  }
  async function openAddress(next) {
    // Editing links select the page without changing its source engine.
    if (!next.output || [CANVAS, 'book', 'design', 'document'].includes(next.output)) {
      const section = next.page ? content.sections[next.page - 1] : active();
      if (!section) throw new Error('주소에 해당하는 페이지가 없습니다.');
      if (section.id !== activeId) await select(section.id);
      else if (viewingBook || previewing) await showActive();
      syncAddress();
    } else await showBook({ output: next.output, page: next.page, push: false });
  }
  /** Opens `value`; `templateId` is the template it was made from (named in the address), if any. */
  async function replace(value, { saved = false, kind = 'canvas', sectionId, view = 'content', templateId = null } = {}) {
    const converted = await convert(value, kind);
    const next = await importSajuCanvas(converted, engine, announce, { store: client, notice: announce });
    serializeContentDocument(next);
    await flushNative(); disposeNative();
    contentSession?.dispose(); contentSession = null; editor?.dispose(); editor = null;
    content = next; generation += 1; title.value = content.title;
    activeId = content.sections.some(section => section.id === sectionId) ? sectionId : content.sections[0]?.id;
    editingView = view === 'design' && active()?.kind === 'penpot' ? 'design' : 'content';
    // The template it was made from names the address from the start (the book view writes it too).
    templateOrigin = templateId;
    if (!activeId) await addSection('document');
    else await showActive();
    dirty = !saved || next !== converted; await emit();
    syncAddress();
  }
  async function refreshDrafts() {
    records = await listLocalDrafts();
    draftSelect.replaceChildren(option('', '저장한 콘텐츠 선택'), ...records.map(record => option(record.id,
      `${record.title}${record.sourceDraftId ? ' · 사본' : ''} · 버전 ${record.revision} · ${new Date(record.updatedAt).toLocaleString('ko-KR')}`)));
    draftSelect.value = records.some(record => record.id === id) ? id : '';
  }
  async function refreshTemplates(selectedId = '') {
    templateRecords = await templates.listSaved();
    const where = { device: '이 기기', account: '내 계정', shared: '공유' };
    savedTemplateSelect.replaceChildren(option('', '저장한 템플릿 선택'), ...templateRecords.map(record => option(record.id,
      `${record.title} · ${where[record.location]}${record.location === 'shared' && !record.mine ? ' · 다른 제작자' : ''}`)));
    const library = templates.remoteStatus();
    templateLibraryHint.textContent = library.error ? `계정 템플릿에 연결하지 못했습니다: ${library.error} 이 기기의 템플릿은 계속 사용할 수 있습니다.`
      : library.connected ? '이 기기와 내 계정의 템플릿, 다른 제작자가 공유한 템플릿을 함께 보여 줍니다.'
      : '로그인하면 템플릿을 내 계정에 저장하거나 모든 제작자와 공유할 수 있습니다.';
    savedTemplateSelect.value = templateRecords.some(record => record.id === selectedId) ? selectedId : '';
  }
  async function saveCurrent({ asCopy = false } = {}) {
    clearTimeout(timer);
    await flushNative();
    const version = generation;
    const result = await saveLocalDraft({ id: asCopy ? uuid() : id, revision: asCopy ? 0 : revision,
      sourceDraftId: asCopy ? id : undefined, document: await emit() });
    id = result.id;
    revision = result.revision; if (generation === version) dirty = false;
    await refreshDrafts(); announce(asCopy ? `현재 작업을 새 사본으로 저장함 · ${new Date(result.updatedAt).toLocaleTimeString('ko-KR')}` : `기기에 저장됨 · ${new Date(result.updatedAt).toLocaleTimeString('ko-KR')}`);
    return { id: result.id, revision: result.revision, title: result.title, updatedAt: result.updatedAt,
      ...(result.sourceDraftId ? { sourceDraftId: result.sourceDraftId } : {}) };
  }
  const save = button('저장', () => saveCurrent(), { class: 'es-work-primary', 'data-testid': 'studio-save' });
  const saveCopy = button('사본 저장', () => saveCurrent({ asCopy: true }), { 'data-testid': 'studio-save-copy', title: '현재 내용을 새 초안으로 저장하고 기존 저장본을 보존합니다' });
  async function captureReaderDocument() {
    // Page-canvas edits already update native snapshots in place. Saving must not
    // mount another editor or leave the page the creator is working on.
    if (viewingBook && content.sections.every(section => section.kind !== 'penpot')) return capture();
    const originalId = activeId, wasPreviewing = previewing;
    if (previewing) await showActive();
    await flushNative(); await capture();
    for (const section of content.sections) {
      if (section.kind !== 'penpot') continue;
      if (activeId !== section.id) await select(section.id);
      await flushNative();
      const state = nativeEditor?.getStatus(), verified = verifiedDesigns.get(section);
      // Saved PNGs have no verified revision in this session. Reopening the
      // original also checks edits made in another tab or by a collaborator.
      if (!state?.connected || !section.preview || !verified || verified.preview !== section.preview ||
          !Number.isSafeInteger(state.revision) || state.revision !== verified.revision) {
        await reflectDesign({ useContentBoard: true });
      }
    }
    // On failure keep the affected original open so the creator can fix it.
    if (activeId !== originalId) await select(originalId);
    const value = await capture();
    if (wasPreviewing) await showPreview(value);
    return value;
  }
  async function saveCurrentTemplate(name, scope = 'device') {
    const record = await templates.save({ name: name || content.title, document: await captureReaderDocument(), scope })
      .catch(reason => { throw reason.code === 'SIGN_IN_REQUIRED' || reason.code === 'INVALID_SCOPE' ? agentError(reason.code, reason.message) : reason; });
    await refreshTemplates(record.id);
    announce(`템플릿 저장됨 · ${record.title} · ${({ device: '이 기기', account: '내 계정', shared: '모든 제작자와 공유' })[record.location]}`);
    return { id: record.id, title: record.title, updatedAt: record.updatedAt, location: record.location };
  }
  async function openBuiltinTemplate(templateId) {
    const entry = templates.findBuiltin(templateId);
    if (!entry) throw agentError('NOT_FOUND', '등록된 기본 템플릿이 없습니다.');
    const sample = getEditorSample(entry.id);
    const kind = sample?.editor === 'penpot' ? 'penpot' : sample?.mode === 'page' ? 'document' : 'canvas';
    const previous = sample?.editor === 'penpot' ? { document: await capture(), reference: content, id, revision, dirty,
      sectionId: activeId, view: editingView, templateId: templateOrigin } : null;
    try {
      await replace(templates.createFromBuiltin(entry.id), { templateId: entry.id, kind, view: kind === 'penpot' ? 'design' : 'content' });
      if (kind === 'penpot') {
        for (let index = 0; index < sample.pages.length; index += 1) {
          const section = content.sections[index];
          if (activeId !== section.id) await select(section.id);
          announce(`Penpot 샘플 원본을 만드는 중 · ${index + 1}/${sample.pages.length}쪽`);
          const native = nativeEditor;
          await materializePenpotTemplateSeed({ editor: native, scene: sample.pages[index].scene,
            assertCurrent: () => {
              if (disposed || nativeEditor !== native || active() !== section) throw agentError('STALE_GENERATION', '샘플을 만드는 중 페이지가 바뀌었습니다.');
            },
            persist: async patch => { Object.assign(section, patch); await emit(); },
          });
        }
        if (activeId !== content.sections[0].id) await select(content.sections[0].id);
        await assertPenpotTemplateReady(nativeEditor);
        await setEditingView('design');
      }
    } catch (reason) {
      if (previous && content !== previous.reference) {
        // A failed new original may also reject flush. Discard its session so
        // restoring the prior document does not depend on saving that failure.
        disposeNative();
        id = previous.id; revision = previous.revision; dirty = previous.dirty;
        await replace(previous.document, { saved: !previous.dirty, sectionId: previous.sectionId,
          view: previous.view, templateId: previous.templateId });
      }
      throw reason;
    }
    id = uuid(); revision = 0; changed();
    await showDefaultView();
    announce(`기본 템플릿으로 새 콘텐츠 생성됨 · ${entry.title} · ${kindLabel(kind)}`);
    return { id: entry.id, title: entry.title, kind: entry.kind, editor: kind };
  }

  async function openSavedTemplate(templateId, { allowSharedPenpotSource = false } = {}) {
    if (templates.findBuiltin(templateId)) return openBuiltinTemplate(templateId);
    const opened = await templates.open(templateId).catch(reason => {
      throw reason.code === 'NOT_FOUND' ? agentError('NOT_FOUND', '저장한 템플릿이 없습니다.') : reason;
    });
    const { record, document: value } = opened;
    if (!allowSharedPenpotSource && value.sections.some(section =>
      (section.kind === 'penpot' && section.source) || (section.alternate?.kind === 'penpot' && section.alternate.source))) {
      throw agentError('SHARED_PENPOT_SOURCE', 'Penpot 원본 파일을 공유하는 템플릿입니다. 원본 수정 위험을 확인한 뒤 allowSharedPenpotSource:true로 다시 열어 주세요.');
    }
    await replace(value, { templateId: record.id }); id = uuid(); revision = 0; changed();
    await showDefaultView();
    await refreshTemplates(templateId);
    announce(`템플릿으로 새 콘텐츠 생성됨 · ${record.title}`);
    return { id: record.id, title: record.title };
  }
  async function moveSavedTemplate(templateId, scope) {
    const moved = await templates.changeScope(templateId, scope).catch(reason => {
      if (['NOT_FOUND', 'READ_ONLY', 'INVALID_SCOPE', 'SIGN_IN_REQUIRED'].includes(reason.code)) throw agentError(reason.code, reason.message);
      throw reason;
    });
    await refreshTemplates(moved.id);
    announce(`템플릿 위치 변경됨 · ${moved.title} · ${({ device: '이 기기', account: '내 계정', shared: '모든 제작자와 공유' })[moved.location]}`);
    return { id: moved.id, title: moved.title, location: moved.location };
  }
  async function removeSavedTemplate(templateId) {
    await templates.remove(templateId).catch(reason => {
      if (reason.code === 'READ_ONLY') throw agentError('READ_ONLY', reason.message);
      throw reason.code === 'NOT_FOUND' ? agentError('NOT_FOUND', '저장한 템플릿이 없습니다.') : reason;
    });
    await refreshTemplates(); announce('템플릿 삭제됨');
  }
  async function setPreview(enabled) {
    if (previewing === enabled) return;
    if (!enabled) { await showActive(); return; }
    await showPreview(await captureReaderDocument());
  }
  /**
   * What the content becomes, where the editor was (책 쪽으로 편집, 결과물): `output` by its
   * address name (book, pdf, lecture), `page` to read from, `read` to read from the page the
   * Studio page being edited prints. Its address is /learn/studio/<template>/<output>/<page>;
   * `push: false` enters it without a history entry of its own (the view a content opens in).
   */
  async function showBook({ output = null, page = null, read = false, push = true } = {}) {
    if (!viewingBook) {
      if (previewing) await showActive();
      await flushNative(); await capture();
      // Pages that print the document's own text edit it in place: the canvas editor, which writes
      // its page back over the document on every capture, is closed meanwhile (showActive opens it again).
      contentSession?.dispose(); contentSession = null; editor?.dispose(); editor = null;
      closeInspector(); root.classList.remove('es-workspace--outline');
      host.hidden = true; preview.hidden = true; bookHost.hidden = false;
      toolbar.hidden = true; canvasNotice.hidden = true; readOnlyNotice.hidden = true;
      root.classList.add('es-workspace--book');
      previewing = true; viewingBook = true; enteringOutput = push; previewButton.textContent = '편집으로 돌아가기';
      renderBookButton();
      announce('결과물 미리보기 · 캔버스의 글과 디자인을 확인합니다');
    }
    await outputPanel.show({ host: bookHost, read, output, page });
  }
  /** Every template starts in the active page's native editor. */
  async function showDefaultView() {
    if (viewingBook || previewing) await showActive();
    syncAddress();
  }
  async function showPreview(value) {
    closeInspector(); root.classList.remove('es-workspace--outline');
    host.hidden = true; preview.hidden = false;
    reader?.dispose(); reader = mountContentReader(preview, value); await reader.ready;
    root.classList.add('es-workspace--reading');
    previewing = true; previewButton.textContent = '편집으로 돌아가기'; announce('콘텐츠 순서대로 읽는 화면');
    toolbar.hidden = true; canvasNotice.hidden = true;
  }
  const previewButton = button('미리보기', () => setPreview(!previewing), { 'data-testid': 'studio-reading' });
  function requireNativeLearning() {
    if (active()?.kind !== 'penpot' || !nativeEditor?.getStatus().connected) throw agentError('PENPOT_NOT_READY', '디자인 편집기에서 레이어를 선택해 주세요.');
    if (previewing) throw agentError('READING_MODE', '편집 화면으로 돌아온 뒤 학습 활동을 연결하세요.');
    return nativeEditor;
  }
  function requireContentSession() {
    if (previewing || viewingBook) throw agentError('READING_MODE', '캔버스 편집 화면으로 돌아온 뒤 내용을 수정하세요.');
    if (active()?.kind === 'penpot') return requireNativeLearning();
    if (!contentSession?.getStatus().connected) throw agentError('CANVAS_NOT_READY', '편집할 캔버스 페이지를 선택해 주세요.');
    return contentSession;
  }
  const targetIdentity = target => Object.fromEntries(['fileId', 'pageId', 'boardId', 'shapeId'].map(key => [key, target[key]]));
  function refreshedLearning(items, targets) {
    if (!Array.isArray(targets) || targets.length !== items.length) throw agentError('INVALID_PENPOT_EXPORT', '학습 레이어의 위치를 확인하지 못했습니다.');
    return items.map((item, index) => {
      const target = targets[index];
      if (!target || Object.entries(targetIdentity(item.target)).some(([key, value]) => target[key] !== value)) throw agentError('INVALID_PENPOT_EXPORT', '다른 레이어의 위치를 학습 활동에 적용할 수 없습니다.');
      return { ...item, target: { ...targetIdentity(target), bounds: structuredClone(target.bounds) } };
    });
  }
  async function selectedLearningTargets() {
    const native = requireNativeLearning();
    const version = generation, section = active();
    const targets = await native.execute('learning_targets', {});
    if (disposed || nativeEditor !== native || active() !== section || generation !== version) throw agentError('STALE_GENERATION', '디자인이 바뀌었습니다. 레이어를 다시 선택하세요.');
    if (!Array.isArray(targets) || targets.length !== 1) throw agentError('INVALID_PENPOT_EXPORT', '학습 활동을 연결할 레이어를 하나 선택해 주세요.');
    return targets;
  }
  async function prepareLearning(items, section) {
    if (section !== active()) throw agentError('INVALID_INPUT', '학습 활동을 연결할 디자인 페이지를 먼저 선택하세요.');
    const version = generation;
    const candidate = structuredClone(content), next = candidate.sections.find(item => item.id === section.id);
    Object.assign(next, { learning: structuredClone(items), preview: null });
    serializeContentDocument(candidate);
    const previous = section.learning ?? [];
    const retained = previous.filter(item => items.some(next => next.id === item.id));
    // Broken references must remain removable, even when another retained
    // layer is missing. Reader preparation validates the remaining targets.
    if (items.length < previous.length && JSON.stringify(items) === JSON.stringify(retained)) {
      return { learning: next.learning, source: next.source, preview: null };
    }
    const native = requireNativeLearning();
    if (items.length) {
      next.learning = refreshedLearning(items, await native.execute('learning_targets', { targets: items.map(item => targetIdentity(item.target)) }));
      next.source.boardId = next.learning[0].target.boardId;
      for (const item of next.learning) {
        if (['fileId', 'pageId', 'boardId'].some(key => item.target[key] !== next.source[key])) throw agentError('INVALID_INPUT', '한 디자인 페이지의 학습 활동은 같은 보드에 연결해 주세요.');
      }
    }
    if (disposed || nativeEditor !== native || active() !== section || generation !== version) throw agentError('STALE_GENERATION', '디자인이 바뀌었습니다. 학습 연결을 다시 저장하세요.');
    serializeContentDocument(candidate);
    return { learning: next.learning, source: next.source, preview: null };
  }
  const learningAuthor = mountLearningAuthor({ getSection: active, run,
    resolveTarget: async () => (await selectedLearningTargets())[0],
    saveItems: async items => {
      const section = active(), patch = await prepareLearning(items, section);
      Object.assign(section, patch); changed(); await emit();
      announce('학습 활동을 연결했습니다 · 독자 보기에서 확인하세요');
    },
  });
  async function reflectDesign({ useContentBoard = false } = {}) {
    const section = active(), native = nativeEditor;
    if (section?.kind !== 'penpot' || !native?.getStatus().connected) throw agentError('PENPOT_NOT_READY', '디자인 편집기가 준비되지 않았습니다. 화면의 안내를 확인해 주세요.');
    if (previewing) throw agentError('READING_MODE', '편집 화면으로 돌아온 뒤 디자인을 반영하세요.');
    await flushNative();
    const version = generation;
    const selection = native.getStatus().selection;
    const options = section.source.boardId && (useContentBoard || !selection?.length) ? { boardId: section.source.boardId } : {};
    if (section.learning?.length) {
      options.targets = section.learning.map(item => targetIdentity(item.target));
      // A bound lesson owns one board, regardless of the layer currently selected.
      options.boardId = section.learning[0].target.boardId;
    }
    announce('선택한 보드를 독자용 콘텐츠에 반영하는 중…');
    let result;
    try { result = await native.execute('export_board', options); }
    catch (reason) {
      if (reason?.code === 'PENPOT_BOARD_REQUIRED') throw agentError('PENPOT_BOARD_REQUIRED', '독자에게 보여줄 보드를 하나 선택해 주세요. 아직 보드가 없다면 보드를 만들고 내용을 배치하세요.');
      throw reason;
    }
    if (disposed || active() !== section || nativeEditor !== native || generation !== version) throw agentError('STALE_GENERATION', '디자인을 내보내는 동안 원본이 바뀌었습니다. 다시 반영해 주세요.');
    if (!result?.source || !result.preview) throw agentError('INVALID_PENPOT_EXPORT', 'Penpot이 원본 위치와 PNG 미리보기를 반환하지 않았습니다.');
    for (const key of ['origin', 'teamId', 'fileId', 'pageId']) {
      if (result.source[key] !== section.source[key]) throw agentError('INVALID_PENPOT_EXPORT', '현재 편집 중인 원본에서 내보낸 디자인만 반영할 수 있습니다.');
    }
    const candidate = structuredClone(content);
    const patch = { source: result.source, preview: result.preview };
    if (section.learning?.length) patch.learning = refreshedLearning(section.learning, result.learningTargets);
    Object.assign(candidate.sections.find(item => item.id === section.id), patch);
    serializeContentDocument(candidate);
    Object.assign(section, structuredClone(patch)); learningAuthor.refresh();
    verifiedDesigns.set(section, { preview: section.preview, revision: native.getStatus().revision });
    changed(); await emit();
    announce('독자용 디자인에 반영했습니다');
    return { source: structuredClone(section.source), width: section.preview.width, height: section.preview.height };
  }
  const reflectDesignButton = button('미리보기 갱신', reflectDesign, { hidden: '', 'data-testid': 'studio-penpot-reflect' });
  function componentReadOnlyReason() {
    if (previewing || viewingBook) return '페이지 편집 화면으로 돌아온 뒤 컴포넌트를 추가해 주세요.';
    if (!active()) return '편집할 페이지를 선택해 주세요.';
    const reason = readOnlyReason(active());
    if (reason) return reason;
    return null;
  }
  // `snapshot` is a calculation already made for these settings (applySubject calculates every page first).
  async function upsertComponent(input, { snapshot: calculated } = {}) {
    const reason = componentReadOnlyReason();
    if (reason) throw agentError('READ_ONLY', reason);
    const section = active(), plugin = getContentComponent(input.type);
    await flushNative(); await capture();
    const existing = input.id === undefined ? null : section.components?.find(item => item.id === input.id);
    if (input.id !== undefined && !existing) throw agentError('NOT_FOUND', '갱신할 컴포넌트를 찾지 못했습니다.');
    if (existing && existing.type !== input.type) throw agentError('INVALID_COMPONENT', '연결된 컴포넌트의 종류는 변경할 수 없습니다.');
    if (!existing && (section.components?.length ?? 0) >= 20) throw agentError('COMPONENT_LIMIT', '페이지당 컴포넌트는 20개까지 추가할 수 있습니다.');
    const props = structuredClone(plugin.validateProps(input.props));
    const version = generation, original = structuredClone(section);
    const snapshot = calculated ? structuredClone(calculated) : !existing || input.recalculate === true || plugin.calculationChanged(existing.props, props)
      ? await plugin.calculate(props, { getAccessToken }) : structuredClone(existing.snapshot);
    if (disposed || active() !== section || version !== generation) throw agentError('STALE_GENERATION', '계산하는 동안 페이지가 변경되었습니다. 다시 시도해 주세요.');
    const instanceId = existing?.id ?? uuid(), scene = plugin.render(props, snapshot);
    const sourceRecord = { id: instanceId, type: plugin.type, version: plugin.version, props, snapshot };
    let transaction;
    try {
      if (section.kind === 'penpot') transaction = await upsertPenpotDataComponent({ editor: nativeEditor, componentId: instanceId,
        scene, record: sourceRecord, binding: existing?.binding ?? null, assertCurrent: () => {
          if (disposed || active() !== section) throw agentError('STALE_GENERATION', '컴포넌트를 추가하는 동안 페이지가 바뀌었습니다.');
        } });
      else transaction = await editor.upsertComponent({ instanceId, scene, record: sourceRecord, binding: existing?.binding });
      const record = { ...sourceRecord, binding: transaction.binding };
      if (section.kind === 'penpot') section.preview = null;
      await capture();
      changed(); componentAuthor.refresh(false, record.id); await emit();
      await transaction.commit?.();
      announce(existing ? '컴포넌트 갱신됨 · 직접 수정한 디자인 유지' : `${plugin.label} 컴포넌트 추가됨`);
      return structuredClone(record);
    } catch (reason) {
      await rollbackComponent(section, original, transaction);
      throw reason;
    }
  }
  // A Saju book's manuscripts (narrative-commands.mjs): listed and written as drafts by agents and the review tab,
  // reviewed only there. Who reviews: the signed-in account (its token's email or subject), or 'local'.
  async function reviewer() {
    try {
      const token = await getAccessToken?.();
      const claims = typeof token === 'string' ? JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) : null;
      const name = claims?.email ?? claims?.sub;
      return typeof name === 'string' && name.trim() ? name.slice(0, 120) : 'local';
    } catch { return 'local'; }
  }
  const narrativeCommands = createNarrativeCommands({ getContent: () => content, getAccessToken, reviewer, asOneChange,
    openPage: async section => { if (activeId !== section.id) await select(section.id); }, upsert: input => upsertComponent(input) });
  /** The components of one person across the content: the one `id` names and every other with the same birth input. */
  function componentsOfPerson(id) {
    const entries = content.sections.flatMap(section => (section.components ?? []).map(component => ({ section, component })));
    const origin = entries.find(entry => entry.component.id === id);
    if (!origin) throw agentError('NOT_FOUND', '기준 컴포넌트를 찾지 못했습니다.');
    return entries.filter(entry => sameSubject(entry.component.props, origin.component.props));
  }
  function personScope(id) {
    const entries = componentsOfPerson(id);
    return { components: entries.length, pages: [...new Set(entries.map(entry => content.sections.indexOf(entry.section) + 1))] };
  }
  /**
   * 명식 한 번에 바꾸기: recalculates every component of one person, on every page, for a new birth input.
   * Every calculation is made first, so nothing changes when the Saju app cannot answer; then only the
   * pages holding those components are opened and updated through the same path as one component.
   */
  async function applySubject({ id, props }) {
    const reason = componentReadOnlyReason();
    if (reason) throw agentError('READ_ONLY', reason);
    await flushNative(); await capture();
    const subject = Object.fromEntries(SUBJECT_KEYS.filter(key => Object.hasOwn(props ?? {}, key)).map(key => [key, props[key]]));
    validateSubject(subject);
    const targets = componentsOfPerson(id);
    const calculations = new Map();
    // One calculation per kind, as every page shows the same person; a kind calculated per component (a book's narrative
    // boxes, each its own scope) once per component.
    const calculationKey = (plugin, component) => plugin.perComponent ? component.id : plugin.type;
    for (const { component } of targets) {
      const plugin = getContentComponent(component.type), key = calculationKey(plugin, component);
      if (!calculations.has(key)) calculations.set(key, await plugin.calculate(plugin.validateProps({ ...component.props, ...subject }), { getAccessToken }));
    }
    const returnTo = activeId, updated = [];
    try {
      for (const section of [...new Set(targets.map(target => target.section))]) {
        if (activeId !== section.id) await select(section.id);
        for (const { component } of targets.filter(target => target.section === section)) {
          await upsertComponent({ id: component.id, type: component.type, props: { ...component.props, ...subject } },
            { snapshot: calculations.get(calculationKey(getContentComponent(component.type), component)) });
        }
        updated.push(content.sections.indexOf(section) + 1);
      }
    } catch (reason) {
      throw Object.assign(reason, { message: `${updated.length ? `${updated.join('·')}쪽은 새 명식으로 바뀌었고, ` : ''}나머지 쪽에서 멈췄습니다: ${reason.message}` });
    }
    if (activeId !== returnTo) await select(returnTo);
    renderOutline();
    const blocked = blockedBookPages(content);
    announce(`${updated.length}개 쪽의 컴포넌트 ${targets.length}개를 새 명식으로 다시 계산했습니다 · ${updated.join('·')}쪽`
      + (blocked.length ? ` · ${blockedBookMessage(blocked, 'PDF로 만들거나 게시할')}` : ''));
    return { pages: updated, components: targets.length, ...(blocked.length ? { blockedBookPages: blocked.map(page => page.index + 1) } : {}) };
  }
  async function rollbackComponent(section, original, transaction) {
    if (transaction?.rollback) await transaction.rollback();
    for (const key of Object.keys(section)) delete section[key];
    Object.assign(section, original);
    if (section.kind !== 'penpot' && !transaction?.rollback) await showActive();
    componentAuthor.refresh();
  }
  async function mutateComponent(instanceId, operation) {
    const reason = componentReadOnlyReason();
    if (reason) throw agentError('READ_ONLY', reason);
    const section = active();
    await flushNative(); await capture();
    const record = section.components?.find(item => item.id === instanceId);
    if (!record) throw agentError('NOT_FOUND', '페이지에서 컴포넌트를 선택해 주세요.');
    const original = structuredClone(section);
    let transaction;
    try {
      if (section.kind === 'penpot') {
        const apply = operation === 'remove' ? removePenpotDataComponent : detachPenpotDataComponent;
        transaction = await apply({ editor: nativeEditor, componentId: record.id, binding: record.binding, assertCurrent: () => {
          if (disposed || active() !== section) throw agentError('STALE_GENERATION', '컴포넌트를 변경하는 동안 페이지가 바뀌었습니다.');
        } });
      } else transaction = await editor[operation === 'remove' ? 'removeComponent' : 'detachComponent'](record.binding);
      if (section.kind === 'penpot') section.preview = null;
      await capture(); changed(); await emit(); await transaction?.commit?.();
      componentAuthor.refresh(true);
      announce(operation === 'remove' ? '컴포넌트 삭제됨' : '일반 콘텐츠로 분리됨 · 직접 편집할 수 있습니다');
    } catch (reason) { await rollbackComponent(section, original, transaction); throw reason; }
  }
  async function changeMode(value) {
    await syncComponents();
    if (active()?.kind !== value && active()?.components?.length) throw agentError('CONNECTED_COMPONENTS', '편집 방식을 옮기기 전에 컴포넌트 패널에서 일반 콘텐츠로 분리해 주세요. 계산 연결과 배치를 보호하기 위한 단계입니다.');
    if (!['document', 'canvas', 'penpot'].includes(value) || !active()) throw agentError('INVALID_INPUT', 'document, canvas 또는 penpot 편집 방식을 선택해 주세요.');
    if (value === active().kind) return;
    if (previewing) await showActive();
    clearTimeout(timer); await emission.catch(() => {});
    await flushNative(); await capture();
    const original = structuredClone(content), originalDirty = dirty;
    const section = active(), index = content.sections.indexOf(section);
    let importTransaction = null;
    transferring = true;
    try {
      if (value !== 'penpot' && section.kind !== 'penpot') {
        section.kind = value;
      } else if (value === 'penpot') {
        const scene = await editor.exportScene();
        scene.title = section.title;
        // Penpot takes a page's pictures as bytes; the ones kept in the asset store are fetched for it.
        scene.assets = await embedStoredAssets(scene.assets);
        const previous = section.alternate;
        const next = {
          id: section.id, title: section.title, kind: 'penpot',
          source: previous ? structuredClone(previous.source) : null,
          preview: null, alt: previous?.alt ?? '',
          ...(previous?.learning ? { learning: structuredClone(previous.learning) } : {}),
          alternate: { kind: section.kind, snapshot: section.snapshot, assets: section.assets },
        };
        content.schemaVersion = 3; content.format = 'mixed'; content.sections[index] = next;
        serializeContentDocument(content);
        disposeNative(); await showActive();
        if (!nativeEditor.getStatus().connected) throw agentError('NATIVE_EDITOR_UNAVAILABLE', 'Penpot 편집기를 열지 못했습니다. 현재 페이지의 원본은 유지됩니다.');
        announce('Penpot으로 글꼴·이미지·레이어를 옮기고 있습니다…');
        const imported = await nativeEditor.execute('import_scene', { scene });
        importTransaction = { editor: nativeEditor, id: imported.transactionId };
        next.source = imported?.source ?? nativeEditor.getSource();
        await nativeEditor.flush();
        serializeContentDocument(content);
      } else {
        if (section.learning?.length) throw agentError('UNSUPPORTED_SCENE', '학습 상호작용이 연결된 디자인은 현재 BlockSuite로 전환할 수 없습니다. Penpot에서 계속 편집해 주세요.');
        announce('BlockSuite로 편집 내용을 옮기고 있습니다…');
        const scene = await nativeEditor.execute('export_scene');
        const converted = await engine.fromScene(scene, { previous: section.alternate });
        const next = {
          id: section.id, title: section.title, kind: value, ...converted,
          alternate: { kind: 'penpot', source: section.source, preview: section.preview, alt: section.alt,
            ...(section.learning ? { learning: section.learning } : {}) },
        };
        const candidate = structuredClone(content); candidate.sections[index] = next;
        serializeContentDocument(candidate);
        disposeNative(); content = candidate;
      }
      if (value !== 'penpot') await showActive();
      changed(); await emit();
      if (importTransaction) await importTransaction.editor.execute('commit_scene', { transactionId: importTransaction.id });
      announce(`${value === 'penpot' ? 'Penpot · 디자인' : value === 'canvas' ? 'BlockSuite · 캔버스' : 'BlockSuite · 문서'} 편집으로 전환했습니다`);
    } catch (reason) {
      if (reason.code === 'PENPOT_SCENE_ROLLBACK_FAILED') {
        throw agentError('PENPOT_ROLLBACK_FAILED', `전환을 완료하지 못했고 디자인 복구도 확인하지 못했습니다. 현재 Penpot 화면에서 원본을 확인해 주세요. ${reason.message}`);
      }
      if (importTransaction) {
        try { await importTransaction.editor.execute('rollback_scene', { transactionId: importTransaction.id }); }
        catch (rollbackError) {
          throw agentError('PENPOT_ROLLBACK_FAILED', `전환을 완료하지 못했고 디자인 복구도 확인하지 못했습니다. 현재 Penpot 화면에서 원본을 확인해 주세요. ${rollbackError.message}`);
        }
      }
      disposeNative(); content = original;
      await showActive(); dirty = originalDirty;
      throw reason;
    } finally {
      transferring = false; clearTimeout(timer);
      mode.value = active().kind; renderOutline();
    }
  }
  const mode = el('select', { 'aria-label': '선택 페이지 편집 방식', 'data-testid': 'studio-editor-mode', onchange: () => {
    const value = mode.value;
    void run(() => changeMode(value)).finally(() => { mode.value = active()?.kind ?? ''; });
  } }, [option('document', 'BlockSuite · 문서'), option('canvas', 'BlockSuite · 캔버스'), option('penpot', 'Penpot · 디자인')]);
  const readOnlyNotice = el('p', { class: 'es-work-hint es-work-chrome es-work-readonly', role: 'note', hidden: '', 'data-testid': 'studio-readonly-notice' });
  const canvasNotice = el('div', { class: 'es-work-hint es-work-chrome', hidden: '', 'data-testid': 'studio-canvas-notice' }, [
    el('span', { text: '같은 페이지의 디자인입니다. 글과 그림의 배치는 캔버스에서 편집할 수 있습니다. ' }),
    button('캔버스 내용 편집', () => changeMode('canvas')),
  ]);
  const upload = el('input', { type: 'file', accept: 'image/png,image/jpeg,image/webp,image/gif', hidden: '', 'data-testid': 'studio-image-file', onchange: () => { void run(async () => {
    const file = upload.files[0]; if (!file) return;
    if (file.size > WORKSPACE_DOCUMENT_LIMITS.assetBytes) throw new Error('이미지 파일 크기가 저장 한도를 넘습니다.');
    if (previewing) await showActive();
    const added = await editor.addImage(file); if (active().kind === 'canvas') editor.selectElements([added]); changed(); upload.value = '';
  }); } });
  async function insertCatalogAsset(asset, options = {}) {
    if (active()?.kind === 'penpot') throw agentError('NATIVE_EDITOR', '카탈로그 이미지는 현재 BlockSuite 문서·캔버스 페이지에 추가할 수 있습니다.');
    if (previewing) await showActive();
    if (!editor) throw agentError('NATIVE_EDITOR', '카탈로그 이미지는 현재 BlockSuite 문서·캔버스 페이지에 추가할 수 있습니다.');
    const src = asset.src || await loadSharedAsset(asset);
    const response = await fetch(src, { cache: 'no-store' });
    if (!response.ok) throw agentError('ASSET_LOAD_FAILED', `이미지 에셋을 불러오지 못했습니다: ${asset.title}`);
    let file = await response.blob();
    if (file.type === '' || file.type === 'application/octet-stream') file = new Blob([file], { type: asset.format });
    else if (file.type !== asset.format) throw agentError('ASSET_FORMAT', `에셋 형식이 카탈로그와 다릅니다: ${asset.title}`);
    if (file.size > WORKSPACE_DOCUMENT_LIMITS.assetBytes || Math.max(asset.dimensions.width, asset.dimensions.height) > 1200) {
      const bitmap = await createImageBitmap(file);
      try {
        const scale = Math.min(1, 1200 / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(bitmap.width * scale); canvas.height = Math.round(bitmap.height * scale);
        canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        file = await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', 0.85));
      } finally { bitmap.close(); }
      if (!file || file.size > WORKSPACE_DOCUMENT_LIMITS.assetBytes) throw agentError('IMAGE_QUOTA', '이 에셋을 이미지 저장 한도 이하로 변환할 수 없습니다.');
    }
    const before = await capture();
    const used = before.sections.reduce((sum, section) => sum + sourceImageBytes(section)
      + (section.alternate ? sourceImageBytes(section.alternate) : 0), 0);
    if (used + file.size > WORKSPACE_DOCUMENT_LIMITS.totalAssetBytes) throw agentError('IMAGE_QUOTA', '이 이미지를 넣으면 콘텐츠의 이미지 저장 한도를 넘습니다.');
    const elementId = await editor.addImage(file, options);
    if (active().kind === 'canvas') editor.selectElements([elementId]);
    changed();
    announce(`${asset.title} 에셋을 페이지에 넣었습니다`);
    return elementId;
  }
  const allAssets = () => [...CONTENT_ASSETS, ...registeredAssets];
  function imageObjectUrl(record) {
    if (!registeredAssetUrls.has(record.id)) {
      const bytes = Uint8Array.from(atob(record.data), character => character.charCodeAt(0));
      registeredAssetUrls.set(record.id, URL.createObjectURL(new Blob([bytes], { type: record.mime })));
    }
    return registeredAssetUrls.get(record.id);
  }
  async function loadSharedAsset(asset) {
    if (asset.src) return asset.src;
    const record = await client.getAsset(asset.id);
    const src = imageObjectUrl(record);
    asset.src = src;
    return src;
  }
  async function refreshRegisteredAssets() {
    const records = await listLocalAssets();
    const local = new Map(records.map(({ data, mime, ...metadata }) => [metadata.id,
      { ...metadata, format: mime, src: imageObjectUrl({ id: metadata.id, data, mime }), scope: 'local' }]));
    const shared = [];
    sharedAssetError = null; sharedAssetAvailable = false;
    if (client.sharedAssetsAvailable && (await client.session()).authenticated) {
      try {
        let cursor = null;
        const visited = new Set();
        do {
          const page = await client.listAssets({ cursor, limit: 200 });
          // A book's pictures are in the store for its pages to name, not to be picked from the library (specs/012).
          shared.push(...page.assets.filter(asset => asset.provenance.source !== SAJU_BOOK_PICTURE_SOURCE));
          if (page.nextCursor !== null && visited.has(page.nextCursor)) throw new Error('이미지 목록 커서가 반복됩니다.');
          cursor = page.nextCursor;
          if (cursor !== null) visited.add(cursor);
        } while (cursor !== null && visited.size < 100);
        if (cursor !== null) throw new Error('이미지 목록이 너무 큽니다. 검색 범위를 좁혀 주세요.');
        sharedAssetAvailable = true;
      } catch (reason) { sharedAssetError = reason instanceof Error ? reason.message : String(reason); }
    }
    registeredAssets = [...local.values()];
    for (const asset of shared) {
      const cached = local.get(asset.id);
      const value = { ...asset, format: asset.mime, scope: 'shared', ...(cached ? { src: cached.src } : {}) };
      if (cached) registeredAssets[registeredAssets.findIndex(item => item.id === asset.id)] = value;
      else registeredAssets.push(value);
    }
    for (const [assetId, url] of registeredAssetUrls) {
      if (registeredAssets.some(asset => asset.id === assetId)) continue;
      URL.revokeObjectURL(url); registeredAssetUrls.delete(assetId);
    }
    assetLibraryHint.textContent = sharedAssetError
      ? `공유 이미지 라이브러리에 연결하지 못했습니다: ${sharedAssetError} 이 기기에 저장된 이미지는 계속 사용할 수 있습니다.`
      : sharedAssetAvailable ? '공유 이미지와 이 기기에 저장된 이미지를 검색합니다. 이미지 삽입은 BlockSuite 문서·캔버스 페이지에서 가능합니다.'
        : '이 기기에 저장된 이미지를 검색합니다. 계정별 공유 이미지 보관함은 아직 연결되지 않았습니다.';
    const subjects = new Set([...CONTENT_ASSETS, ...registeredAssets].map(asset => asset.subject));
    for (const subject of subjects) if (![...assetSubject.options].some(item => item.value === subject)) assetSubject.append(option(subject, subject));
    renderAssetGallery();
  }
  async function registerAsset(input) {
    const required = (value, name, max = 200) => {
      const text = agentString(value, name, max).trim();
      if (!text) throw agentError('INVALID_INPUT', `${name}은 비어 있을 수 없습니다.`);
      return text;
    };
    const strings = (value, name) => {
      if (!Array.isArray(value) || !value.length || value.length > 20) throw agentError('INVALID_INPUT', `${name}은 1~20개의 설명이어야 합니다.`);
      return value.map((item, index) => required(item, `${name}[${index}]`, 200));
    };
    const title = required(input.title, 'title');
    const description = required(input.description, 'description', 1000);
    const subject = required(input.subject, 'subject', 100);
    const topic = required(input.topic, 'topic', 200);
    const concepts = strings(input.concepts, 'concepts');
    const recommendedUses = strings(input.recommendedUses, 'recommendedUses');
    const avoidUses = strings(input.avoidUses, 'avoidUses');
    const keywords = strings(input.keywords, 'keywords');
    const source = required(input.source, 'source', 500);
    if (!['generated', 'authored', 'imported'].includes(input.sourceKind)) throw agentError('INVALID_INPUT', 'sourceKind는 generated, authored, imported 중 하나여야 합니다.');
    if (!['icon', 'scene', 'diagram', 'texture', 'background', 'frame'].includes(input.role)) throw agentError('INVALID_INPUT', '올바른 이미지 역할(role)이 필요합니다.');
    const issues = [];
    validateWorkspaceAsset({ mime: input.mime, data: input.data }, 'image', (path, message) => issues.push(`${path}: ${message}`));
    if (issues.length) throw agentError('INVALID_INPUT', issues.join('; '));
    const bytes = Uint8Array.from(atob(input.data), character => character.charCodeAt(0));
    let bitmap;
    try { bitmap = await createImageBitmap(new Blob([bytes], { type: input.mime })); }
    catch { throw agentError('INVALID_IMAGE', '해독 가능한 이미지 파일이 필요합니다.'); }
    const dimensions = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    if (!dimensions.width || !dimensions.height || dimensions.width > 8192 || dimensions.height > 8192) throw agentError('INVALID_IMAGE', '이미지 크기는 각 변이 1~8192픽셀이어야 합니다.');
    const hash = [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))].map(value => value.toString(16).padStart(2, '0')).join('');
    const record = { id: `local-${hash}`, title, label: title, description, subject, topic, concepts,
      recommendedUses, avoidUses, keywords, role: input.role, dimensions, mime: input.mime, data: input.data,
      provenance: { kind: input.sourceKind, source, date: new Date().toISOString().slice(0, 10) },
      updatedAt: new Date().toISOString() };
    const existing = (await listLocalAssets()).find(asset => asset.id === record.id);
    if (existing) {
      const comparable = asset => ({ ...asset, updatedAt: null, provenance: { ...asset.provenance, date: null } });
      if (JSON.stringify(comparable(existing)) !== JSON.stringify(comparable(record)))
        throw agentError('ASSET_CONFLICT', '이미 등록된 이미지에 다른 메타데이터가 있습니다. 라이브러리에서 확인해 주세요.');
    } else await saveLocalAsset(record);
    const savedRecord = existing ?? record;
    let shared = false, syncError = null;
    if (client.sharedAssetsAvailable && (await client.session()).authenticated) {
      try { await client.registerAsset(savedRecord); shared = true; }
      catch (reason) { syncError = reason instanceof Error ? reason.message : String(reason); }
    }
    await refreshRegisteredAssets();
    announce(shared ? `${title} 에셋이 공유 라이브러리에 등록됐습니다`
      : `${title} 에셋이 이 기기에 저장됐습니다${syncError ? ` · 공유 등록 실패: ${syncError}` : ''}`);
    return { ...registeredAssets.find(asset => asset.id === record.id), scope: shared ? 'shared' : 'local', ...(syncError ? { syncError } : {}) };
  }
  const toolbar = el('div', { class: 'es-work-toolbar es-work-chrome', 'aria-label': '편집 도구' }, [
    button('↶', () => editor.undo(), { 'aria-label': '실행 취소', title: '실행 취소' }),
    button('↷', () => editor.redo(), { 'aria-label': '다시 실행', title: '다시 실행' }),
    el('span', { class: 'es-work-separator' }),
    button('T  텍스트', () => { const added = editor.addText('텍스트를 입력하세요'); if (active().kind === 'canvas') editor.selectElements([added]); changed(); }, { 'data-testid': 'studio-add-text' }),
    button('텍스트 다듬기', () => typography.open(true), { 'data-testid': 'studio-text-style', 'data-canvas-only': '' }),
    button('□  도형', () => { editor.selectElements([editor.addShape('rect')]); changed(); }, { 'data-testid': 'studio-add-shape', 'data-canvas-only': '' }),
    button('○', () => { editor.selectElements([editor.addShape('ellipse')]); changed(); }, { 'aria-label': '원 추가', 'data-canvas-only': '' }),
    button('이미지', () => upload.click(), { 'data-testid': 'studio-add-image' }), upload,
    el('span', { class: 'es-work-toolbar-note', text: '직접 선택하고, 움직이고, 다듬으세요' }),
  ]);
  function visiblePanels() {
    if (root.dataset.panels !== 'auto') return root.dataset.panels;
    return compactViewport.matches || (active()?.kind === 'penpot' && editingView === 'design') ? 'none' : 'both';
  }
  function renderPanels() {
    const panels = visiblePanels();
    const left = panels === 'both' || panels === 'outline';
    const right = panels === 'both' || panels === 'inspector';
    compositionButton.setAttribute('aria-expanded', String(left));
    inspectorButton.setAttribute('aria-expanded', String(right && !designPanel.hidden));
    materialsButton.setAttribute('aria-expanded', String(right && !materialsPanel.hidden));
    learningButton.setAttribute('aria-expanded', String(right && !learningPanel.hidden));
    componentButton.setAttribute('aria-expanded', String(right && !componentPanel.hidden));
    root.classList.toggle('es-workspace--outline', left);
    root.classList.toggle('es-workspace--materials', right);
    nativeLayersButton.setAttribute('aria-expanded', String(root.dataset.nativePanel === 'left'));
    nativePropertiesButton.setAttribute('aria-expanded', String(root.dataset.nativePanel === 'right'));
  }
  async function fitNativeViewport() {
    renderPanels();
    if (nativeEditor?.getStatus().connected && !previewing) await nativeEditor.execute('set_editing_view', { view: editingView });
  }
  function closeInspector() {
    const panels = visiblePanels();
    root.dataset.panels = panels === 'both' || panels === 'outline' ? 'outline' : 'none';
    renderPanels();
  }
  async function togglePanel(side, opener, tab = 'design') {
    const panels = visiblePanels();
    const alreadyOpen = side === 'outline' ? panels === 'outline' || panels === 'both'
      : (panels === 'inspector' || panels === 'both') && !({ design: designPanel, materials: materialsPanel, learning: learningPanel, components: componentPanel }[tab]).hidden;
    const keepOther = !compactViewport.matches && (panels === 'both' || panels === (side === 'outline' ? 'inspector' : 'outline'));
    root.dataset.panels = alreadyOpen ? keepOther ? side === 'outline' ? 'inspector' : 'outline' : 'none' : keepOther ? 'both' : side;
    root.dataset.nativePanel = 'none'; panelOpener = opener;
    if (side === 'inspector') { showInspector(tab); learningAuthor.refresh(); }
    await fitNativeViewport();
  }
  async function dismissPanels() {
    root.dataset.panels = 'none'; root.dataset.nativePanel = 'none';
    await fitNativeViewport(); pendingFocus = panelOpener;
  }
  async function toggleNativePanel(side, opener) {
    root.dataset.nativePanel = root.dataset.nativePanel === side ? 'none' : side;
    root.dataset.panels = 'none'; panelOpener = opener;
    await fitNativeViewport();
  }
  const compositionButton = button('페이지', () => togglePanel('outline', compositionButton), { 'aria-expanded': 'false', 'data-testid': 'studio-composition' });
  const inspectorButton = button('내용·설정', () => togglePanel('inspector', inspectorButton), { 'aria-expanded': 'false', 'data-testid': 'studio-inspector-toggle' });
  const materialsButton = button('자료·강좌', () => togglePanel('inspector', materialsButton, 'materials'), { 'aria-expanded': 'false', 'data-testid': 'studio-materials-toggle' });
  const learningButton = button('학습 활동', () => togglePanel('inspector', learningButton, 'learning'), { 'aria-expanded': 'false', 'data-testid': 'studio-learning-toggle' });
  const componentButton = button('컴포넌트', () => togglePanel('inspector', componentButton, 'components'), { 'aria-expanded': 'false', 'data-testid': 'studio-components-toggle' });
  const nativeLayersButton = button('레이어', () => toggleNativePanel('left', nativeLayersButton), { class: 'es-work-native-toggle', 'aria-expanded': 'false', 'data-testid': 'studio-native-layers' });
  const nativePropertiesButton = button('디자인 속성', () => toggleNativePanel('right', nativePropertiesButton), { class: 'es-work-native-toggle', 'aria-expanded': 'false', 'data-testid': 'studio-native-properties' });
  const contentViewButton = button('콘텐츠 편집', () => setEditingView('content'), { 'aria-pressed': 'true', 'data-testid': 'studio-content-view' });
  const designViewButton = button('디자인 편집', () => setEditingView('design'), { 'aria-pressed': 'false', 'data-testid': 'studio-design-view' });
  // Shown for a Penpot page (renderEditingView), so none before a page is open, nor for a content edited on its output's pages.
  const taskViews = el('div', { class: 'es-work-task-views', role: 'group', 'aria-label': '작업 화면', hidden: '' }, [contentViewButton, designViewButton]);
  const addPageMenu = el('details', { class: 'es-work-add-menu' }, [el('summary', { text: '+ 페이지' }),
    el('div', { class: 'es-work-add-menu-options' }, [
      button('캔버스 페이지', async () => { addPageMenu.open = false; await addSection('canvas'); }, { 'data-testid': 'studio-add-canvas' }),
      button('디자인 페이지 · Penpot', async () => { addPageMenu.open = false; editingView = 'design'; await addSection('penpot'); }, { 'data-testid': 'studio-add-penpot' }),
      button('글 문서 · BlockSuite', async () => { addPageMenu.open = false; await addSection('document'); }, { 'data-testid': 'studio-add-block-document' }),
    ]),
  ]);
  // Opening a saved template whose Penpot source is shared asks before it opens.
  async function openSavedAsked(templateId) {
    try { await openSavedTemplate(templateId); }
    catch (reason) {
      if (reason.code !== 'SHARED_PENPOT_SOURCE') throw reason;
      if (!window.confirm('이 템플릿의 Penpot 디자인 원본은 같은 파일을 가리킵니다. 디자인을 직접 수정하면 원본에도 반영됩니다. 새 콘텐츠를 열까요?')) return;
      await openSavedTemplate(templateId, { allowSharedPenpotSource: true });
    }
  }
  const templatePicker = mountTemplatePicker({ families: CONTENT_TEMPLATE_PRESETS,
    listSaved: async () => { await refreshTemplates(savedTemplateSelect.value); return templateRecords; },
    openBuiltin: templateId => run(async () => { if (mayReplace()) await openBuiltinTemplate(templateId); }),
    openSaved: templateId => run(async () => { if (mayReplace()) await openSavedAsked(templateId); }),
    pageCountsOf: card => templatePageCounts(card.familyId, card.example, templates.createFromBuiltin(card.id)) });
  templatePicker.trigger.addEventListener('click', () => { void run(() => templatePicker.show()); });
  const outputPanel = mountOutputPanel({ outputsFor, getContent: () => content, getDocument: () => capture(), getActivePage: () => activeId,
    selectPage: sectionId => run(() => select(sectionId)),
    onLeave: () => run(() => showActive()),
    // The output shown and the page read are the rest of the address.
    onView: view => {
      outputView = view; syncAddress({ push: enteringOutput }); enteringOutput = false;
      // Back in the output's page list, no page is in view.
      if (view.page === null && readingPage !== null) { readingPage = null; readingSectionId = null; markCurrent(); renderPager(); }
    },
    // The page list marks what the book page being read is printed from: its Studio page, or its page template.
    onRead: (sectionId, page) => {
      readingSectionId = sectionId; readingPage = page?.number ?? null;
      markCurrent(); renderPager();
    },
    // The page templates of the pages shown (a preview may number them differently) are the page list.
    onTemplates: templates => { shownTemplates = templates; renderOutline(); },
    // Text the book pages print from the document itself was edited in it.
    onDocumentEdited: () => changed(),
    // Edited on its output's pages, a content has no Studio page to open from them.
    opensStudioPages: () => !pagesEdited(),
    hooks: {
      // 강좌에 올리기: the materials panel's course section, where content is saved into a lecture.
      openCourse: () => run(async () => {
        // A content edited on its output's pages stays there; the materials open beside them.
        if (viewingBook && !pagesEdited()) await showActive();
        const panels = visiblePanels();
        if (!((panels === 'inspector' || panels === 'both') && !materialsPanel.hidden)) await togglePanel('inspector', materialsButton, 'materials');
        cms.open = true;
        cms.scrollIntoView({ block: 'start' });
      }),
      openReading: () => run(async () => { if (viewingBook) await showActive(); await setPreview(true); }),
    } });
  // 결과물 opens the outputs' page list and the toggle reads (or, for a book, edits) its pages, both where the editor was.
  outputPanel.trigger.addEventListener('click', () => { void run(() => showBook()); });
  const bookViewButton = button('결과물로 보기', () => (viewingBook ? showActive() : showBook({ read: true })), { 'data-testid': 'studio-book-view-toggle' });
  // The book's page count, once known, rides on its button: 결과물로 보기 · 46쪽.
  const bookPageCounts = new Map();
  function renderBookButton() {
    const first = outputsFor(content)[0];
    for (const control of [addPageMenu, inspectorButton, learningButton]) control.hidden = false;
    bookViewButton.hidden = !first;
    previewButton.hidden = false;
    if (!first) return;
    const count = bookPageCounts.get(`${first.output.id}|${first.subject.id}`);
    bookViewButton.setAttribute('aria-pressed', String(viewingBook));
    bookViewButton.textContent = viewingBook ? '캔버스 편집으로 돌아가기' : `PDF · 결과물${count ? ` · ${count}쪽` : ''}`;
  }
  outputChanged = () => {
    outputPanel.update();
    renderBookButton();
    const first = outputsFor(content)[0];
    const key = first && `${first.output.id}|${first.subject.id}`;
    if (!first?.output.pageCount || bookPageCounts.has(key)) return;
    bookPageCounts.set(key, null);
    void first.output.pageCount(first.subject).then(pages => { bookPageCounts.set(key, pages); renderBookButton(); }, () => bookPageCounts.delete(key));
  };
  outputChanged();
  // The main flow — start from a template, see what it becomes — comes before the page tools.
  const flowActions = el('div', { class: 'es-work-flow', role: 'group', 'aria-label': '템플릿과 결과물' }, [templatePicker.trigger, outputPanel.trigger, bookViewButton]);
  const header = el('header', { class: 'es-work-header es-work-chrome' }, [
    el('div', { class: 'es-work-identity' }, [el('a', { class: 'es-work-brand', href: '/ko/learn', text: 'EduSense' }),
      el('span', { class: 'es-work-divider', text: '/' }), title]),
    el('div', { class: 'es-work-header-actions' }, [status, previewButton, saveCopy, save]),
    el('div', { class: 'es-work-page-context' }, [pageKind, pageSelect, pager, taskViews,
      el('div', { class: 'es-work-page-actions' }, [flowActions, compositionButton, inspectorButton, nativeLayersButton, nativePropertiesButton, materialsButton, learningButton, componentButton, reflectDesignButton,
        addPageMenu]),
    ]),
  ]);
  const topic = el('input', { placeholder: '어떤 이야기를 만들까요?', 'data-testid': 'studio-topic' });
  const source = el('textarea', { rows: 5, placeholder: '참고 자료나 수업 노트를 붙여 넣으세요.', 'data-testid': 'studio-source' });
  const template = el('select', { 'aria-label': '콘텐츠 스타일' }, CONTENT_TEMPLATE_PRESETS.map(preset => option(preset.id, preset.label)));
  template.value = 'learning-editorial';
  const aiPanel = el('details', { class: 'es-work-panel' }, [el('summary', { text: '주제와 자료로 시작' }),
    field('주제', topic), field('원문 자료', source), field('스타일', template),
    button('원문으로 문서 만들기', async () => {
      if (!mayReplace()) return;
      const value = arrangeSourceText({ topic: topic.value, sourceText: source.value, templateId: template.value });
      await replace(value, { kind: 'document' }); id = uuid(); revision = 0; changed();
    }, { 'data-testid': 'studio-arrange' }),
    button('AI 초안 생성', async () => {
      if (!topic.value.trim()) throw new Error('만들 주제를 입력해 주세요.');
      if (!mayReplace()) return;
      announce('AI가 자료를 바탕으로 초안을 작성하고 있습니다…');
      const value = await client.generate({ topic: topic.value.trim(), sourceText: source.value, templateId: template.value });
      await replace(value); id = uuid(); revision = 0; changed();
      announce('AI 초안 생성됨 · 내용과 출처를 확인해 주세요');
    }, { 'data-testid': 'studio-generate' }), el('p', { class: 'es-work-hint', text: 'AI 초안 생성은 서버 연결이 필요합니다.' }),
  ]);
  const templateName = el('input', { 'aria-label': '새 템플릿 이름', placeholder: '현재 콘텐츠를 템플릿으로 저장', maxlength: 200, 'data-testid': 'studio-template-name' });
  const savedTemplateSelect = el('select', { 'aria-label': '저장한 템플릿', 'data-testid': 'studio-template-select' });
  const templateScope = el('select', { 'aria-label': '템플릿 저장 위치', 'data-testid': 'studio-template-scope' },
    [option('device', '이 기기에만'), option('account', '내 계정 (나만 보기)'), option('shared', '모든 제작자와 공유')]);
  const templateLibraryHint = el('p', { class: 'es-work-hint', 'data-testid': 'studio-template-library' });
  const templateMoveScope = el('select', { 'aria-label': '선택한 템플릿 옮길 위치', 'data-testid': 'studio-template-move-scope' },
    [option('account', '내 계정 (나만 보기)'), option('shared', '모든 제작자와 공유')]);
  const templatePanel = el('details', { class: 'es-work-panel', open: '' }, [el('summary', { text: '템플릿으로 시작' }),
    button('템플릿 고르기…', () => templatePicker.show(), { class: 'es-work-primary', 'data-testid': 'studio-template-browse' }),
    el('p', { class: 'es-work-hint', text: `기본 템플릿 ${CONTENT_TEMPLATE_PRESETS.reduce((sum, preset) => sum + preset.examples.length, 0)}종과 저장한 템플릿을 한곳에서 찾습니다.` }),
    field('새 템플릿 이름', templateName), field('저장 위치', templateScope),
    button('현재 콘텐츠를 템플릿으로 저장', async () => {
      const name = templateName.value.trim() || content.title.trim();
      if (!name) throw new Error('템플릿 이름이나 콘텐츠 제목을 입력해 주세요.');
      await saveCurrentTemplate(name, templateScope.value);
    }, { 'data-testid': 'studio-template-save' }),
    savedTemplateSelect,
    button('선택한 템플릿으로 새 콘텐츠 만들기', async () => {
      const record = templateRecords.find(item => item.id === savedTemplateSelect.value);
      if (!record) throw new Error('저장한 템플릿을 선택해 주세요.');
      if (!mayReplace()) return;
      await openSavedAsked(record.id);
    }, { 'data-testid': 'studio-template-open' }),
    field('선택한 템플릿 옮길 위치', templateMoveScope),
    button('선택한 템플릿 위치 바꾸기', async () => {
      const record = templateRecords.find(item => item.id === savedTemplateSelect.value);
      if (!record) throw new Error('옮길 템플릿을 선택해 주세요.');
      await moveSavedTemplate(record.id, templateMoveScope.value);
    }, { 'data-testid': 'studio-template-move' }),
    button('선택한 템플릿 삭제', async () => {
      const record = templateRecords.find(item => item.id === savedTemplateSelect.value);
      if (!record) throw new Error('삭제할 템플릿을 선택해 주세요.');
      if (!window.confirm(`“${record.title}” 템플릿을 삭제할까요?`)) return;
      await removeSavedTemplate(record.id);
    }, { 'data-testid': 'studio-template-delete' }),
    templateLibraryHint,
    el('p', { class: 'es-work-hint', text: '템플릿은 원본 스냅샷으로 보관됩니다. 저장 위치가 이 기기이면 이 브라우저에만, 내 계정·공유이면 서버에 저장됩니다. BlockSuite 글·캔버스는 새 편집 원본으로 복사됩니다. Penpot 디자인은 원본 파일을 공유하므로 독립 편집하려면 Penpot에서 파일을 복제해 연결하세요.' }),
  ]);
  let visibleAssetCount = 12;
  const resetAssetGallery = () => { visibleAssetCount = 12; renderAssetGallery(); };
  const assetSearch = el('input', { type: 'search', placeholder: '개념·그림·용도 검색', 'aria-label': '이미지 에셋 검색', 'data-testid': 'studio-asset-search', oninput: resetAssetGallery });
  const assetSubject = el('select', { 'aria-label': '이미지 에셋 교과', 'data-testid': 'studio-asset-subject', onchange: resetAssetGallery }, [
    option('', '모든 주제'), ...[...new Set(CONTENT_ASSETS.map(asset => asset.subject))].map(subject => option(subject, subject)),
  ]);
  const assetResults = el('div', { class: 'es-work-asset-results', 'aria-live': 'polite', 'data-testid': 'studio-asset-results' });
  function renderAssetGallery() {
    const assets = searchContentAssets({ query: assetSearch.value, subject: assetSubject.value, assets: allAssets() });
    const supported = active()?.kind !== 'penpot';
    assetResults.replaceChildren(...assets.slice(0, visibleAssetCount).map(asset => {
      const insert = button('페이지에 넣기', () => insertCatalogAsset(asset), {
        'data-testid': `studio-insert-asset-${asset.id}`, disabled: !supported ? '' : undefined,
        'aria-label': `${asset.title} 이미지 에셋을 페이지에 넣기`,
      });
      const picture = el('img', { ...(asset.src ? { src: asset.src } : {}), alt: asset.description, loading: 'lazy' });
      if (!asset.src && asset.scope === 'shared') void loadSharedAsset(asset).then(src => { if (!disposed) picture.src = src; }).catch(reason => { picture.alt = `${asset.title}: 이미지 미리보기를 불러오지 못했습니다 (${reason.message})`; });
      return el('article', { class: 'es-work-asset-card', 'data-asset-id': asset.id }, [
        picture,
        el('div', { class: 'es-work-asset-copy' }, [
          el('strong', { text: asset.title }),
          el('small', { text: `${asset.subject} · ${asset.topic}${asset.scope === 'shared' ? ' · 공유' : asset.scope === 'local' ? ' · 이 기기' : ''}` }),
          el('p', { text: asset.description }),
          el('p', { class: 'es-work-asset-avoid', text: `주의: ${asset.avoidUses[0]}` }),
        ]), insert,
      ]);
    }));
    if (!assets.length) assetResults.append(el('p', { class: 'es-work-hint', text: '조건에 맞는 에셋이 없습니다.' }));
    if (assets.length > visibleAssetCount) assetResults.append(button(`더 보기 · ${assets.length - visibleAssetCount}개 남음`, () => { visibleAssetCount += 12; renderAssetGallery(); }, { class: 'es-work-asset-more', 'data-testid': 'studio-asset-more' }));
  }
  const assetLibraryHint = el('p', { class: 'es-work-hint', text: '이미지 라이브러리를 확인하고 있습니다…' });
  const assetPanel = el('details', { class: 'es-work-panel es-work-asset-panel', open: '' }, [
    el('summary', { text: '재사용 이미지 에셋' }), assetSearch, assetSubject,
    assetLibraryHint, assetResults,
  ]);
  renderAssetGallery();
  const importFile = el('input', { type: 'file', accept: '.md,.json', 'data-testid': 'studio-import', onchange: () => { void run(async () => {
    const file = importFile.files[0]; if (!file) return;
    if (file.size > WORKSPACE_DOCUMENT_LIMITS.jsonBytes + 128) throw new Error('콘텐츠 파일 크기가 저장 한도를 넘습니다.');
    const text = await file.text();
    const value = file.name.endsWith('.json') ? JSON.parse(text) : parseContentDocument(text);
    if (!value) throw new Error('EduSense 콘텐츠 파일을 선택해 주세요.');
    serializeContentDocument(value);
    if (!mayReplace()) return;
    await replace(value); id = uuid(); revision = 0; changed(); importFile.value = '';
  }); } });
  const library = el('details', { class: 'es-work-panel' }, [el('summary', { text: '저장한 콘텐츠' }), draftSelect,
    button('열기', async () => {
      const record = records.find(record => record.id === draftSelect.value);
      if (!record) throw new Error('저장한 콘텐츠를 선택해 주세요.');
      if (!mayReplace()) return;
      await replace(readLocalDraft(record), { saved: true }); id = record.id; revision = record.revision;
      await showDefaultView(); announce(`저장한 콘텐츠 · 버전 ${revision}`);
    }, { 'data-testid': 'studio-open' }),
    button('원본 파일 내보내기', async () => {
      await flushNative(); const value = await capture();
      const url = URL.createObjectURL(new Blob([serializeContentDocument(value)], { type: 'text/markdown;charset=utf-8' }));
      const link = el('a', { href: url, download: `${(value.title || 'edusense-content').replace(/[\\/:*?"<>|]/g, '-')}.md` });
      link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000); announce(value.sections.some(section => section.kind === 'penpot') ? '콘텐츠 파일 내보냄 · Penpot 편집 원본은 연결된 서버에 보관됩니다' : '편집 가능한 원본을 내보냈습니다');
    }, { 'data-testid': 'studio-export' }), field('원본 파일 가져오기', importFile),
    el('p', { class: 'es-work-hint', text: '기기 저장은 현재 브라우저에 보관됩니다. Penpot 디자인은 독자용 이미지와 원본 위치를 저장합니다. Penpot 편집 원본 전체는 Penpot의 파일 내보내기를 사용하세요.' }),
  ]);
  const courseSelect = el('select', { 'aria-label': '내 강좌 초안', 'data-testid': 'studio-course-select' }, [option('', '새 강좌 만들기')]);
  const lectureSelect = el('select', { 'aria-label': '편집할 강의' });
  const cmsStatus = el('p', { class: 'es-work-hint es-work-cms-status', role: 'status', 'data-testid': 'studio-cms-status', text: '강좌 저장은 초안으로 보관됩니다. 검증 후 게시하면 학습자가 볼 수 있습니다.' });
  const cmsIssues = el('ul', { class: 'es-work-cms-status', hidden: '', 'data-testid': 'studio-cms-issues' });
  const cmsReader = el('a', { hidden: '', text: '게시한 강좌 보기', target: '_blank', rel: 'noopener', 'data-testid': 'studio-cms-reader' });
  const cmsLogin = el('a', { class: 'es-work-cms-login',
    href: `/ko/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`,
    text: '로그인', 'data-testid': 'studio-cms-login' });
  const cmsSession = el('p', { class: 'es-work-hint', role: 'status', 'data-testid': 'studio-cms-session', text: '로그인 상태 확인 중…' });
  async function refreshLogin() {
    const session = await client.session();
    if (disposed) return;
    cmsLogin.hidden = session.authenticated;
    cmsSession.textContent = session.authenticated ? '로그인됨 · 내 강좌 저장·게시 사용 가능' : '서버에 저장하려면 EduSense 계정으로 로그인해 주세요.';
  }
  cmsLogin.addEventListener('click', event => {
    event.preventDefault();
    void run(async () => { await flushNative(); await preserveSession(); window.location.assign(cmsLogin.href); });
  });
  function fillLectures() {
    lectureSelect.replaceChildren(option('', '새 강의로 추가'), ...(courseDraft?.sections ?? []).flatMap(section => (section.lectures ?? [])
      .filter(lecture => lecture.id).map(lecture => option(lecture.id, `${section.title} / ${lecture.title}`))));
  }
  function renderCourseStatus() {
    const published = courseDraft && publishedDraftIds.has(courseDraft.id);
    cmsStatus.textContent = courseDraft ? `${courseDraft.title} · ${published ? '게시 완료' : '초안'} · 최근 저장 ${new Date(courseDraft.updatedAt).toLocaleString('ko-KR')}`
      : '현재 편집 내용으로 새 강좌를 만들거나 내 초안을 열 수 있습니다.';
    cmsReader.hidden = !courseDraft?.publishedCourseId;
    if (courseDraft?.publishedCourseId) cmsReader.href = `/ko/learn/course/${encodeURIComponent(courseDraft.publishedCourseId)}`;
  }
  async function refreshCourseList() {
    const selected = courseSelect.value || courseId;
    const items = await client.list();
    publishedDraftIds = new Set(items.filter(item => item.isPublished).map(item => item.id));
    courseSelect.replaceChildren(option('', '새 강좌 만들기'), ...items.map(item => option(item.id, `${item.title}${item.isPublished ? ' · 게시됨' : ''}`)));
    if (items.some(item => item.id === selected)) courseSelect.value = selected;
    renderCourseStatus();
  }
  function showValidation(report) {
    cmsIssues.replaceChildren(...report.issues.map(issue => el('li', { text: issue.message })));
    cmsIssues.hidden = report.issues.length === 0;
    announce(report.publishable ? '게시 조건을 충족했습니다' : '게시 전에 표시된 내용을 확인해 주세요');
    return report.publishable;
  }
  async function saveCourseContent() {
    if (!courseDraft) throw new Error('강좌를 먼저 열어 주세요.');
    const value = await captureReaderDocument();
    if (!value.title.trim() || !value.sections.length) throw new Error('제목과 내용을 작성해 주세요.');
    let lectureId = lectureSelect.value, next;
    if (lectureId) next = updateCreatorLecture(courseDraft, lectureId, value);
    else {
      next = structuredClone(courseDraft);
      next.sections ??= [];
      if (!next.sections.length) next.sections.push({ title: '학습 자료', order: 1, lectures: [] });
      const section = next.sections[0]; section.lectures ??= [];
      lectureId = uuid();
      section.lectures.push({ id: lectureId, title: value.title, order: section.lectures.length + 1, contentType: 'MARKDOWN',
        markdownContent: serializeContentDocument(value) });
    }
    courseDraft = await client.save(courseDraft, next); courseId = courseDraft.id;
    fillLectures(); lectureSelect.value = lectureId;
    cmsIssues.hidden = true; renderCourseStatus(); announce('서버에 초안을 저장했습니다');
  }
  const cms = el('details', { class: 'es-work-panel' }, [el('summary', { text: '내 강좌 저장·게시' }), cmsSession, cmsLogin,
    field('내 강좌 초안', courseSelect), button('목록 새로고침', refreshCourseList, { 'data-testid': 'studio-cms-list' }),
    button('강좌 열기 / 만들기', async () => {
      if (courseSelect.value) courseDraft = await client.getDraft(courseSelect.value);
      else {
        const value = await captureReaderDocument();
        if (!value.title.trim() || !value.sections.length) throw new Error('새 강좌를 만들려면 제목과 내용을 작성해 주세요.');
        courseDraft = await client.create(value);
      }
      courseId = courseDraft.id; fillLectures();
      lectureSelect.value = courseDraft.sections?.[0]?.lectures?.[0]?.id ?? '';
      cmsIssues.hidden = true; renderCourseStatus(); await refreshCourseList();
    }, { 'data-testid': 'studio-cms-open' }),
    lectureSelect, button('선택 강의 가져오기', async () => {
      if (!courseDraft || !lectureSelect.value) throw new Error('강좌와 강의를 선택해 주세요.');
      const lecture = (courseDraft.sections ?? []).flatMap(section => section.lectures ?? []).find(lecture => lecture.id === lectureSelect.value);
      let value = parseContentDocument(lecture.markdownContent ?? '');
      if (!value) value = arrangeSourceText({ topic: lecture.title, sourceText: lecture.markdownContent ?? '', templateId: 'learning-editorial' });
      if (!mayReplace()) return;
      await replace(value, { kind: 'document' }); id = uuid(); revision = 0; changed();
    }), button('강좌에 저장', saveCourseContent, { 'data-testid': 'studio-cms-save' }),
    el('div', { class: 'es-work-cms-actions' }, [
      button('게시 전 기본 점검', async () => {
        if (!courseDraft) throw new Error('강좌를 먼저 열어 주세요.');
        showValidation(client.validate(courseDraft));
      }, { 'data-testid': 'studio-cms-validate' }),
      button('서버 상태 새로고침', async () => {
        if (!courseId) throw new Error('강좌를 먼저 열어 주세요.');
        const lectureId = lectureSelect.value;
        courseDraft = await client.getDraft(courseId); fillLectures(); lectureSelect.value = lectureId;
        renderCourseStatus(); await refreshCourseList();
      }, { 'data-testid': 'studio-cms-refresh' }),
      button('저장하고 게시', async () => {
        if (!courseDraft) throw new Error('강좌를 먼저 열어 주세요.');
        if (publishedDraftIds.has(courseId)) throw new Error('이미 게시한 강좌입니다. 새 강좌를 만들어 게시해 주세요.');
        await saveCourseContent();
        if (!showValidation(client.validate(courseDraft))) return;
        const result = await client.publish(courseId);
        courseDraft = { ...courseDraft, publishedCourseId: result.courseId };
        publishedDraftIds.add(courseId);
        renderCourseStatus(); await refreshCourseList(); announce('강좌를 게시했습니다');
      }, { class: 'es-work-primary', 'data-testid': 'studio-cms-publish' }),
    ]), cmsStatus, cmsIssues, cmsReader,
  ]);
  const typography = mountPenpotTypography({ getEditor: () => editor, getKind: () => active()?.kind, fail,
    apply(elementId, delta, properties) {
      if (elementId) editor.setTextContent(elementId, delta, properties);
      else { const added = editor.addRichText(delta, properties); if (active()?.kind === 'canvas') editor.selectElements([added]); }
      changed();
    },
  });
  const altText = el('textarea', { rows: 3, maxlength: 2000, 'aria-label': '디자인 대체 텍스트', oninput: () => { if (active()?.kind === 'penpot') { active().alt = altText.value; changed(); } } });
  const altField = field('디자인 대체 텍스트', altText);
  const pageSettings = el('details', { class: 'es-work-panel es-work-page-settings' }, [
    el('summary', { text: '페이지 설정' }), field('이름', sectionTitle), altField,
    el('div', { class: 'es-work-row' }, [button('위로', async () => {
      const index = content.sections.findIndex(section => section.id === activeId);
      if (index > 0) { [content.sections[index - 1], content.sections[index]] = [content.sections[index], content.sections[index - 1]]; renderOutline(); changed(); }
    }), button('아래로', async () => {
      const index = content.sections.findIndex(section => section.id === activeId);
      if (index < content.sections.length - 1) { [content.sections[index + 1], content.sections[index]] = [content.sections[index], content.sections[index + 1]]; renderOutline(); changed(); }
    }), button('삭제', async () => {
      if (content.sections.length === 1) throw new Error('콘텐츠에는 한 페이지 이상이 필요합니다.');
      if (!window.confirm('선택한 페이지를 삭제할까요?')) return;
      await flushNative();
      if (nativeSectionId === activeId) disposeNative();
      contentSession?.dispose(); contentSession = null; editor?.dispose(); editor = null;
      content.sections = content.sections.filter(section => section.id !== activeId); activeId = content.sections[0].id;
      await showActive(); changed();
    })]),
    el('details', { class: 'es-work-source-settings' }, [el('summary', { text: '원본 편집 방식' }),
      field('기존 원본을 다른 편집기로 옮기기', mode),
      el('p', { class: 'es-work-hint', text: '콘텐츠·디자인 편집 전환은 같은 원본을 사용합니다. 여기에서 편집기를 바꾸면 지원되는 내용만 변환되며, 기존 원본은 함께 보관됩니다.' }),
    ]),
  ]);
  const nativeContent = mountNativeContentPanel({
    getNative: () => previewing || viewingBook ? null : active()?.kind === 'penpot' ? nativeEditor : contentSession,
    getSectionId: () => activeId,
    getImageLimit: () => active()?.kind === 'canvas' ? WORKSPACE_DOCUMENT_LIMITS.assetBytes : 1024 * 1024,
    run: action => run(action, { propagate: true }),
    onEdited: () => { if (active()?.kind === 'penpot') active().preview = null; changed(); canvasProperties.refresh(); },
    onDesign: () => { if (active()?.kind === 'penpot') void run(() => setEditingView('design')); else canvasProperties.element.scrollIntoView({ block: 'nearest' }); },
  });
  const canvasProperties = mountCanvasProperties({ getEditor: () => active()?.kind === 'canvas' && !previewing && !viewingBook ? editor : null,
    run, onEdited: changed });
  const legacyNotice = el('p', { class: 'es-work-hint' , text: '본문을 클릭해 글을 수정하세요. 캔버스 페이지에서는 글·이미지·도형을 선택해 위치와 디자인을 바꿀 수 있습니다.' });
  const componentAuthor = mountComponentPanel({ getSection: active, canEdit: componentReadOnlyReason, run, upsert: input => upsertComponent(input),
    remove: id => mutateComponent(id, 'remove'), detach: id => mutateComponent(id, 'detach'), applySubject, personScope,
    selectElements: ids => { if (active()?.kind === 'canvas' && ids.length) editor?.selectElements(ids); }, narrative: narrativeCommands });
  const panelId = `studio-inspector-${uuid()}`;
  const designPanel = el('div', { role: 'tabpanel', id: `${panelId}-design`, 'aria-labelledby': `${panelId}-design-tab` }, [
    nativeContent.element, canvasProperties.element, legacyNotice, pageSettings,
  ]);
  const agentConnection = mountAgentConnection({ execute: (operation, input) => agentCommands.command(operation, input), getLabel: () => content.title || 'EduSense 콘텐츠 제작실' });
  const materialsPanel = el('div', { role: 'tabpanel', hidden: '', id: `${panelId}-materials`, 'aria-labelledby': `${panelId}-materials-tab` }, [
    assetPanel, templatePanel, aiPanel, library, cms, el('p', { class: 'es-work-hint', text: `이미지는 개당 ${WORKSPACE_DOCUMENT_LIMITS.assetBytes / 1024 / 1024}MiB, 콘텐츠 전체 ${WORKSPACE_DOCUMENT_LIMITS.totalAssetBytes / 1024 / 1024}MiB까지 저장할 수 있습니다.` }),
  ]);
  const learningPanel = el('div', { role: 'tabpanel', hidden: '', id: `${panelId}-learning`, 'aria-labelledby': `${panelId}-learning-tab` }, [learningAuthor.element]);
  const componentPanel = el('div', { role: 'tabpanel', hidden: '', id: `${panelId}-components`, 'aria-labelledby': `${panelId}-components-tab` }, [componentAuthor.element]);
  function showInspector(tab) {
    componentPanel.hidden = tab !== 'components';
    if (tab === 'components') componentAuthor.refresh();
    designPanel.hidden = tab !== 'design'; materialsPanel.hidden = tab !== 'materials'; learningPanel.hidden = tab !== 'learning';
    for (const [button, key] of [[designTab, 'design'], [materialsTab, 'materials'], [learningTab, 'learning'], [componentTab, 'components']]) {
      button.setAttribute('aria-selected', String(tab === key)); button.tabIndex = tab === key ? 0 : -1;
    }
    renderPanels();
  }
  const designTab = button('페이지', () => showInspector('design'), { role: 'tab', id: `${panelId}-design-tab`, 'aria-controls': `${panelId}-design`, 'aria-selected': 'true', 'data-testid': 'studio-design-tab' });
  const materialsTab = button('자료', () => showInspector('materials'), { role: 'tab', id: `${panelId}-materials-tab`, 'aria-controls': `${panelId}-materials`, 'aria-selected': 'false', tabindex: -1, 'data-testid': 'studio-materials-tab' });
  const learningTab = button('학습', () => { showInspector('learning'); learningAuthor.refresh(); }, { role: 'tab', id: `${panelId}-learning-tab`, 'aria-controls': `${panelId}-learning`, 'aria-selected': 'false', tabindex: -1, 'data-testid': 'studio-learning-tab' });
  const componentTab = button('컴포넌트', () => showInspector('components'), { role: 'tab', id: `${panelId}-components-tab`, 'aria-controls': `${panelId}-components`, 'aria-selected': 'false', tabindex: -1, 'data-testid': 'studio-components-tab' });
  const tabs = el('div', { class: 'es-work-inspector-tabs', role: 'tablist', 'aria-label': '속성과 자료' }, [designTab, materialsTab, learningTab, componentTab]);
  tabs.addEventListener('keydown', event => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const keys = ['design', 'materials', 'learning', 'components'], buttons = [designTab, materialsTab, learningTab, componentTab];
    const current = buttons.findIndex(button => button.getAttribute('aria-selected') === 'true');
    const index = event.key === 'Home' ? 0 : event.key === 'End' ? 3 : (current + (event.key === 'ArrowRight' ? 1 : 3)) % 4;
    showInspector(keys[index]); learningAuthor.refresh(); buttons[index].focus();
  });
  const inspector = el('aside', { class: 'es-work-inspector es-work-chrome', 'aria-label': '디자인 속성과 자료' }, [
    button('패널 닫기', dismissPanels, { class: 'es-work-inspector-close', 'aria-label': '보조 패널 닫기', 'data-testid': 'studio-inspector-close' }),
    tabs, designPanel, materialsPanel, learningPanel, componentPanel,
  ]);
  const navigation = el('aside', { class: 'es-work-navigation es-work-chrome', 'aria-label': '콘텐츠 구성' }, [
    el('div', { class: 'es-work-nav-heading' }, [pagesHeading, button('새로', async () => {
      if (!mayReplace()) return; await flushNative(); disposeNative(); contentSession?.dispose(); contentSession = null; editor?.dispose(); editor = null;
      content = { schemaVersion: 2, format: 'blocksuite', title: '', sections: [] }; title.value = ''; id = uuid(); revision = 0; editingView = 'design';
      templateOrigin = null; syncAddress(); await addSection('penpot');
    }, { 'data-testid': 'studio-new' })]), outline, addPages,
    nativeContent.listElement,
  ]);
  const bookAddressPath = el('code', { 'data-testid': 'studio-book-address' });
  const bookAddressCopy = el('button', { type: 'button', class: 'es-work-book-address-copy', 'data-testid': 'studio-book-address-copy', text: '주소 복사',
    onclick: () => {
      void navigator.clipboard?.writeText(window.location.href).then(() => { bookAddressCopy.textContent = '복사됨'; setTimeout(() => { bookAddressCopy.textContent = '주소 복사'; }, 1500); },
        () => { bookAddressCopy.textContent = '복사하지 못함'; });
    } });
  const bookAddress = el('p', { class: 'es-work-book-address', hidden: '' }, [el('span', { text: '이 화면 주소' }), bookAddressPath, bookAddressCopy]);
  const bookHost = el('div', { class: 'es-work-book', hidden: '', 'data-testid': 'studio-book-view' }, [bookAddress]);
  const stage = el('section', { class: 'es-work-stage', 'aria-label': '편집 영역' }, [toolbar, readOnlyNotice, canvasNotice, host, preview, bookHost]);
  root.classList.add('es-workspace');
  root.dataset.panels = 'auto'; root.dataset.nativePanel = 'none';
  root.replaceChildren(header, error, el('div', { class: 'es-work-body' }, [navigation, stage, inspector]), el('footer', { class: 'es-work-footer es-work-chrome' }, [
    el('span', { text: 'EduSense 콘텐츠 제작실' }), agentConnection.element,
  ]), typography.element);
  const beforeUnload = event => {
    if (dirty && (recoveredGeneration !== generation || nativeEditor?.getStatus().dirty)) { event.preventDefault(); event.returnValue = ''; }
  };
  const preserveOnHide = () => { if (document.visibilityState === 'hidden') { clearTimeout(timer); if (!busy) void preserveSession().catch(fail); } };
  const dismissOnEscape = event => {
    if (event.key !== 'Escape' || (visiblePanels() === 'none' && root.dataset.nativePanel === 'none')) return;
    event.preventDefault(); void run(dismissPanels);
  };
  const resizePanels = () => { root.dataset.panels = 'auto'; root.dataset.nativePanel = 'none'; renderPanels(); if (!busy) void run(fitNativeViewport); };
  root.addEventListener('keydown', dismissOnEscape);
  compactViewport.addEventListener('change', resizePanels);
  window.addEventListener('beforeunload', beforeUnload);
  window.addEventListener('popstate', followAddress);
  document.addEventListener('visibilitychange', preserveOnHide);
  void refreshLogin();
  const ready = run(async () => {
    engine = await import('./blocksuite-engine.mjs?v=document-canvas-1');
    if (disposed) return;
    let initial = parseContentDocument(initialMarkdown);
    if (!initial && initialMarkdown.trim()) initial = arrangeSourceText({ topic: '', sourceText: initialMarkdown, templateId: 'learning-editorial' });
    let restored = false, missing = null;
    if (initial) await replace(initial, { saved: true });
    // An address naming a template opens it: its latest working copy on this device, else the template.
    else if (address?.templateId) {
      let session = null;
      try { session = await findTemplateSession(address.templateId); } catch (reason) { fail(reason); }
      if (session) {
        id = session.draftId; revision = session.revision;
        await replace(session.document, { saved: !session.dirty, sectionId: session.activeSectionId, view: session.editingView, templateId: address.templateId });
        restored = true;
      } else if (templates.findBuiltin(address.templateId)) {
        await openBuiltinTemplate(address.templateId);
      } else {
        try { await openSavedAsked(address.templateId); }
        catch (reason) { if (reason.code !== 'NOT_FOUND') throw reason; missing = address.templateId; }
      }
    }
    if (!initial && (!address?.templateId || missing)) {
      let session;
      try { session = await readWorkspaceSession(previousSessionId); } catch (reason) { fail(reason); }
      let drafts = [];
      try { drafts = await listLocalDrafts(); } catch (reason) { fail(reason); }
      if (session) {
        const saved = drafts.find(record => record.id === session.draftId);
        if (!session.dirty && saved && saved.revision > session.revision) {
          session.document = readLocalDraft(saved); session.revision = saved.revision;
        }
        id = session.draftId; revision = session.revision;
        await replace(session.document, { saved: !session.dirty, sectionId: session.activeSectionId, view: session.editingView, templateId: session.templateId ?? null });
        restored = true;
      } else if (drafts.length) {
        const latest = drafts[0]; id = latest.id; revision = latest.revision;
        await replace(readLocalDraft(latest), { saved: true }); restored = true;
      } else { await addSection('document'); dirty = false; }
    }
    initialized = true;
    syncAddress();
    if (address?.output && templateOrigin === address.templateId) await openAddress(address);
    else if (restored) await showDefaultView();
    await refreshDrafts();
    await refreshTemplates();
    await refreshRegisteredAssets();
    if (missing) announce(`주소의 템플릿(${missing})을 찾지 못해 이전 작업을 열었습니다`);
    else if (restored) announce(dirty ? '이전 작업 복원됨 · 자동 보관된 내용' : '이전 작업 복원됨');
    else if (active()?.kind !== 'penpot') announce('편집 준비 완료');
  });
  const agentCommands = createAgentCommandQueue({
    getGeneration: () => generation,
    waitUntilReady: async () => { await ready; if (activeRun) await activeRun; },
    execute: executeAgentOperation,
  });
  function agentState() {
    return {
      title: content.title, generation, dirty, draftId: id, revision, activeSectionId: activeId, previewing, editingView,
      sections: content.sections.map(section => ({ id: section.id, title: section.title, kind: section.kind })),
      components: structuredClone(active()?.components ?? []),
      blocks: editor?.getBlocks() || [], layers: editor?.getLayers() || [], selection: editor?.getSelection() || [],
      penpot: active()?.kind === 'penpot' ? nativeEditor?.getStatus() ?? null : null,
    };
  }
  function requireReplacement(input) {
    if (dirty && input.discardChanges !== true) throw agentError('UNSAVED_CHANGES', '저장하지 않은 내용이 있습니다. save_local로 저장하거나 discardChanges:true를 명시하세요.');
  }
  function requireEditor() {
    if (previewing) throw agentError('READING_MODE', 'preview를 false로 전환한 뒤 편집하세요.');
    if (!editor) throw agentError('NATIVE_EDITOR', '이 명령은 BlockSuite 문서·캔버스 명령입니다. 디자인 객체는 Penpot 원본 명령으로 편집하세요.');
    return editor;
  }
  function elementOptions(value = {}, kind) {
    agentObject(value, 'options');
    const geometry = ['x', 'y', 'width', 'height'];
    const style = ['fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'color', 'textAlign'];
    const keys = kind === 'text-update' ? style : kind === 'text' ? [...geometry, ...style, 'type']
      : kind === 'shape' ? [...geometry, 'fillColor', 'strokeColor', 'strokeWidth', 'rotate'] : [...geometry, 'caption'];
    for (const key of Object.keys(value)) if (!keys.includes(key)) throw agentError('INVALID_INPUT', `지원하지 않는 options 속성: ${key}`);
    const result = { ...value };
    if (geometry.some(key => Object.hasOwn(value, key))) {
      if (active().kind !== 'canvas' && kind === 'text') throw agentError('INVALID_INPUT', '문서 텍스트에는 자유 배치 좌표를 지정할 수 없습니다. 캔버스 페이지를 선택하세요.');
      if (!geometry.every(key => Number.isFinite(value[key]) && Math.abs(value[key]) <= 1000000) || value.width < 1 || value.height < 1 || value.width > 100000 || value.height > 100000) throw agentError('INVALID_INPUT', '배치는 x,y,width,height를 모두 숫자로 지정해야 합니다.');
      result.xywh = JSON.stringify(geometry.map(key => value[key]));
      delete result.x; delete result.y;
      if (kind !== 'image') { delete result.width; delete result.height; }
    }
    for (const key of ['fillColor', 'strokeColor']) if (Object.hasOwn(value, key) && !(typeof value[key] === 'string' && CSS.supports('color', value[key]))) throw agentError('INVALID_INPUT', `${key} 색상이 올바르지 않습니다.`);
    if (Object.hasOwn(value, 'strokeWidth') && !(Number.isFinite(value.strokeWidth) && value.strokeWidth >= 0 && value.strokeWidth <= 100)) throw agentError('INVALID_INPUT', 'strokeWidth는 0~100이어야 합니다.');
    if (Object.hasOwn(value, 'rotate') && !(Number.isFinite(value.rotate) && Math.abs(value.rotate) <= 360)) throw agentError('INVALID_INPUT', 'rotate는 -360~360이어야 합니다.');
    if (Object.hasOwn(value, 'caption')) agentString(value.caption, 'caption', 2000);
    if (Object.hasOwn(value, 'type') && (!['text', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'quote'].includes(value.type) || active().kind === 'canvas')) throw agentError('INVALID_INPUT', 'type은 문서의 문단 형식으로만 지정할 수 있습니다.');
    return result;
  }
  async function executeAgentOperation(operation, input, { mutation }) {
    let extra = {}; const readGeneration = generation;
    const action = async () => {
      switch (operation) {
        case 'inspect': await capture(); break;
        case 'get_document': extra.document = await capture(); break;
        case 'list_assets': {
          const query = input.query === undefined ? '' : agentString(input.query, 'query', 200);
          const subject = input.subject === undefined ? '' : agentString(input.subject, 'subject', 100);
          await refreshRegisteredAssets();
          extra.assets = searchContentAssets({ query, subject, assets: allAssets() });
          extra.assetLibrary = { sharedAvailable: sharedAssetAvailable, ...(sharedAssetError ? { error: sharedAssetError } : {}) }; break;
        }
        case 'list_templates':
          extra.templates = (await templates.listSaved()).map(record => ({ id: record.id, title: record.title, updatedAt: record.updatedAt,
            location: record.location, mine: record.mine }));
          extra.templateLibrary = templates.remoteStatus();
          extra.builtinTemplates = templates.listBuiltins(); break;
        case 'learning_targets': extra.targets = await selectedLearningTargets(); break;
        case 'content_items': extra.content = await requireContentSession().execute('content_items'); break;
        case 'design_shapes':
          extra.design = await requireNativeLearning().execute('design_shapes', { fileId: input.fileId, pageId: input.pageId }); break;
        case 'list_drafts':
          await refreshDrafts(); extra.drafts = records.map(record => ({ id: record.id, title: record.title, revision: record.revision, updatedAt: record.updatedAt,
            ...(record.sourceDraftId ? { sourceDraftId: record.sourceDraftId } : {}) })); break;
        case 'read_draft': {
          const record = (await listLocalDrafts()).find(record => record.id === agentString(input.draftId, 'draftId', 200));
          if (!record) throw agentError('NOT_FOUND', '저장한 콘텐츠가 없습니다.');
          extra.draft = { id: record.id, revision: record.revision, title: record.title, updatedAt: record.updatedAt,
            ...(record.sourceDraftId ? { sourceDraftId: record.sourceDraftId } : {}) };
          extra.document = readLocalDraft(record); break;
        }
        case 'create_document': {
          requireReplacement(input);
          const name = agentString(input.title, 'title'); const kind = input.kind ?? 'document';
          if (!['document', 'canvas', 'penpot'].includes(kind)) throw agentError('INVALID_INPUT', '지원하지 않는 페이지 종류입니다.');
          await flushNative(); disposeNative(); contentSession?.dispose(); contentSession = null; editor?.dispose(); editor = null;
          content = { schemaVersion: 2, format: 'blocksuite', title: name, sections: [] }; title.value = name; id = uuid(); revision = 0;
          templateOrigin = null; syncAddress();
          await addSection(kind); break;
        }
        case 'replace_document':
          requireReplacement(input); agentObject(input.document, 'document'); serializeContentDocument(input.document);
          await replace(input.document); id = uuid(); revision = 0; changed(); break;
        case 'set_title': content.title = agentString(input.title, 'title'); title.value = content.title; changed(); break;
        case 'add_section':
          if (!['document', 'canvas', 'penpot'].includes(input.kind)) throw agentError('INVALID_INPUT', '지원하지 않는 페이지 종류입니다.');
          if (input.title !== undefined) agentString(input.title, 'title');
          await addSection(input.kind);
          if (input.title !== undefined) { active().title = input.title; sectionTitle.value = input.title; renderOutline(); changed(); }
          extra.sectionId = activeId; break;
        case 'select_section':
          if (!content.sections.some(section => section.id === input.sectionId)) throw agentError('NOT_FOUND', '선택할 페이지가 없습니다.');
          await select(input.sectionId); break;
        case 'switch_mode': await changeMode(input.mode); break;
        case 'upsert_component': extra.component = await upsertComponent(input); break;
        case 'apply_subject': extra.applied = await applySubject(input); break;
        case 'list_narratives': Object.assign(extra, await narrativeCommands.list(input)); break;
        case 'write_narrative': extra.written = await narrativeCommands.write(input); break;
        case 'remove_component': await mutateComponent(input.id, 'remove'); break;
        case 'detach_component': await mutateComponent(input.id, 'detach'); break;
        case 'set_editing_view': await setEditingView(input.view); break;
        case 'penpot_mcp': {
          const native = requireNativeLearning();
          const source = native.getStatus();
          if (input.fileId !== source.fileId || input.pageId !== source.pageId) throw agentError('PENPOT_SCOPE_CHANGED', '현재 디자인 파일·페이지와 요청이 다릅니다. inspect로 확인해 주세요.');
          agentString(input.tool, 'tool', 200); agentObject(input.arguments, 'arguments');
          const { userToken } = await native.execute('connect_mcp');
          try {
            extra.upstream = await callPenpotMcp({ userToken, fileId: input.fileId, pageId: input.pageId, tool: input.tool, arguments: input.arguments });
          } finally {
            // A failed upstream script can have applied some edits. Persist the
            // original event queue before acknowledging either result or error.
            await native.flush();
            await nativeContent.refresh();
          }
          break;
        }
        case 'create_design_shape':
        case 'update_design_shape': {
          const native = requireNativeLearning();
          const command = { fileId: input.fileId, pageId: input.pageId };
          for (const key of ['parentId', 'type', 'x', 'y', 'width', 'height', 'name', 'fillColor', 'id', 'expected', 'changes']) {
            if (Object.hasOwn(input, key)) command[key] = input[key];
          }
          extra.design = await native.execute(operation, command);
          active().preview = null; changed();
          await nativeContent.refresh(); break;
        }
        case 'select_content':
        case 'update_content': {
          const native = requireContentSession();
          const command = { id: input.id, fileId: input.fileId, pageId: input.pageId };
          if (operation === 'update_content') {
            for (const key of ['text', 'expectedText', 'image']) if (Object.hasOwn(input, key)) command[key] = input[key];
          }
          extra.content = await native.execute(operation, command);
          if (operation === 'update_content') { if (active().kind === 'penpot') active().preview = null; changed(); }
          await nativeContent.refresh(); break;
        }
        case 'update_section': {
          const section = content.sections.find(section => section.id === (input.sectionId ?? activeId));
          if (!section) throw agentError('NOT_FOUND', '편집할 페이지가 없습니다.');
          if (input.title !== undefined) agentString(input.title, 'title');
          if (input.alt !== undefined) { if (section.kind !== 'penpot') throw agentError('INVALID_INPUT', 'alt는 Penpot 페이지에서 지정합니다.'); agentString(input.alt, 'alt', 2000); }
          const patch = input.learning === undefined ? {} : await prepareLearning(input.learning, section);
          Object.assign(section, patch);
          if (input.title !== undefined) section.title = input.title;
          if (input.alt !== undefined) section.alt = input.alt;
          if (section.id === activeId) { sectionTitle.value = section.title; if (section.kind === 'penpot') altText.value = section.alt; }
          renderOutline(); learningAuthor.refresh(); changed(); break;
        }
        case 'insert_text': extra.elementId = requireEditor().addRichText(input.delta, elementOptions(input.options, 'text')); changed(); break;
        case 'update_text': requireEditor().setTextContent(agentString(input.id, 'id'), input.delta, elementOptions(input.options, 'text-update')); changed(); break;
        case 'add_shape': extra.elementId = requireEditor().addShape(input.shapeType ?? 'rect', elementOptions(input.options, 'shape')); changed(); break;
        case 'add_image': {
          const current = requireEditor(), options = elementOptions(input.options, 'image');
          if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(input.mime) || typeof input.data !== 'string' || input.data.length > Math.ceil(WORKSPACE_DOCUMENT_LIMITS.assetBytes / 3) * 4 || input.data.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(input.data)) throw agentError('INVALID_INPUT', '저장 한도 이하 PNG/JPEG/WebP/GIF의 base64 데이터가 필요합니다.');
          const bytes = Uint8Array.from(atob(input.data), character => character.charCodeAt(0));
          const before = await capture();
          const existingBytes = before.sections.reduce((sum, section) => sum + sourceImageBytes(section)
            + (section.alternate ? sourceImageBytes(section.alternate) : 0), 0);
          if (bytes.length > WORKSPACE_DOCUMENT_LIMITS.assetBytes || existingBytes + bytes.length > WORKSPACE_DOCUMENT_LIMITS.totalAssetBytes) throw agentError('IMAGE_QUOTA', '콘텐츠의 이미지 저장 한도를 넘습니다.');
          extra.elementId = await current.addImage(new Blob([bytes], { type: input.mime }), options); changed(); break;
        }
        case 'insert_asset': {
          requireEditor();
          const assetId = agentString(input.assetId, 'assetId', 100);
          await refreshRegisteredAssets();
          const asset = allAssets().find(item => item.id === assetId);
          if (!asset) throw agentError('NOT_FOUND', '등록된 이미지 에셋이 없습니다. list_assets로 카탈로그를 확인해 주세요.');
          extra.elementId = await insertCatalogAsset(asset, elementOptions(input.options, 'image'));
          extra.asset = asset;
          break;
        }
        case 'register_asset': extra.asset = await registerAsset(input); break;
        case 'update_element': requireEditor().updateElementProperties(agentString(input.id, 'id'), agentObject(input.properties, 'properties')); changed(); break;
        case 'select_elements': requireEditor().selectElements(input.ids); break;
        case 'undo':
        case 'redo':
          if (active()?.kind === 'penpot') {
            if (previewing) throw agentError('READING_MODE', '편집 화면으로 돌아온 뒤 실행하세요.');
            await nativeEditor.execute(operation);
          } else requireEditor()[operation]();
          break;
        case 'save_local': extra.draft = await saveCurrent(); break;
        case 'save_local_copy': extra.draft = await saveCurrent({ asCopy: true }); break;
        case 'save_template':
          extra.template = await saveCurrentTemplate(input.name === undefined ? content.title : agentString(input.name, 'name'),
            input.scope === undefined ? 'device' : agentString(input.scope, 'scope', 20)); break;
        case 'open_template':
          requireReplacement(input);
          extra.template = await openSavedTemplate(agentString(input.templateId, 'templateId', 100), { allowSharedPenpotSource: input.allowSharedPenpotSource === true }); break;
        case 'set_template_scope':
          extra.template = await moveSavedTemplate(agentString(input.templateId, 'templateId', 100), agentString(input.scope, 'scope', 20)); break;
        case 'delete_template':
          await removeSavedTemplate(agentString(input.templateId, 'templateId', 100)); break;
        case 'open_draft': {
          requireReplacement(input); await refreshDrafts(); const record = records.find(record => record.id === input.draftId);
          if (!record) throw agentError('NOT_FOUND', '저장한 콘텐츠가 없습니다.');
          await replace(readLocalDraft(record), { saved: true }); id = record.id; revision = record.revision; await showDefaultView(); break;
        }
        case 'preview':
          if (typeof input.enabled !== 'boolean') throw agentError('INVALID_INPUT', 'enabled는 true 또는 false여야 합니다.');
          await setPreview(input.enabled); break;
        case 'import_penpot':
          extra.imported = await reflectDesign(); break;
      }
      if (mutation && !['save_local', 'save_local_copy', 'save_template', 'delete_template', 'set_template_scope', 'register_asset', 'import_penpot'].includes(operation)) await emit();
    };
    if (mutation) await run(action, { propagate: true }); else await action();
    if (!mutation && generation !== readGeneration) throw agentError('STALE_SNAPSHOT', '읽는 동안 편집 내용이 변경되었습니다. 다시 읽어 주세요.');
    return { state: agentState(), ...extra };
  }
  return {
    ready,
    getDocument: capture,
    flush,
    setDocument: value => run(() => replace(value)),
    executeAgentCommand: agentCommands.command,
    destroy() { disposed = true; root.removeEventListener('keydown', dismissOnEscape); compactViewport.removeEventListener('change', resizePanels); delete root.dataset.panels; delete root.dataset.nativePanel; agentCommands.dispose(); agentConnection.dispose(); nativeContent.dispose(); clearTimeout(timer); window.removeEventListener('beforeunload', beforeUnload); window.removeEventListener('popstate', followAddress); document.removeEventListener('visibilitychange', preserveOnHide); contentSession?.dispose(); editor?.dispose(); disposeNative(); reader?.dispose(); outputPanel.dispose(); typography.dispose(); for (const url of registeredAssetUrls.values()) URL.revokeObjectURL(url); root.replaceChildren(); root.classList.remove('es-workspace', 'es-workspace--penpot', 'es-workspace--outline', 'es-workspace--materials', 'es-workspace--content'); delete root.dataset.editingView; },
  };
}
